// src/views/TimesheetsView.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, Timestamp, collectionGroup, getDocs, doc, getDoc, addDoc, updateDoc, setDoc, serverTimestamp, arrayUnion, writeBatch } from 'firebase/firestore'; // arrayUnion for non-lossy editHistory append — e0b7d4a1; writeBatch for atomic archive+delete — a9f5027c
import { startOfWeek, endOfWeek, addDays, subDays, format, startOfDay, differenceInDays, eachDayOfInterval } from 'date-fns';
import { resolveProfileRead } from '../utils/profilePrivacy'; // profcut-4b9d2e63 — time off lives in users/{id}/private/profile
import { canCorrectJobTime, canViewPayroll, mustAskOwnerForOwnTime } from '../utils/permissions'; // tsmgr-5a2c8e71; mustAskOwnerForOwnTime: s9selfapprove-8d1f4b26
import { shiftDurationHours, isShiftPaid } from '../utils/shiftTime'; // 5b8c2e9d — shared clocked-time calc; isShiftPaid — paidhrs-4a1 3c read-side frozen flag (twin file; see its header)
import { csvCell } from '../utils/csvExport'; // s9csv-4e1b7c28 — payroll export formula guard
import { closedPunchHoursByDay, sumDayBuckets } from '../utils/jobPunchDays'; // jobday-6b2f9e14 — splits a job's cumulative clocked total across the days its punches STARTED (see that file's header)
import { CheckCircle2, ChevronLeft, ChevronRight, ChevronDown, Clock, Briefcase, AlertCircle, AlertTriangle, TrendingUp, TrendingDown, Download, DollarSign, Users, ChevronUp } from 'lucide-react';

// jobday-6b2f9e14 — HOW FAR EITHER SIDE OF THE PERIOD TO REACH FOR JOBS.
//
// Jobs are queried by startAt (their SCHEDULED day), but their hours are now attributed to
// the day each punch STARTED. So a job booked outside the period can legitimately hold work
// done inside it, and a plain in-period query would miss it entirely — the hours would be
// absent from Actual, Efficiency, Non-job time and the CSV.
//
// This is a HEURISTIC and is meant to be read as one. A production census (all 30 orgs, 148
// punches) measured the gap between a punch's day and its job's scheduled day at -16 to +27
// days, so 31 covers every case on record with margin. A punch drifting further stays
// invisible — exactly as invisible as it is today, so this is never a regression, but it is
// not a proof either.
//
// The exact fix is server-written firstPunchAt/lastPunchAt bounds on the job plus a second
// query, which needs a backend seam, a backfill and an index. Deferred (see BACKLOG) with the
// trigger: any punch drifting past this constant, or a job collection large enough that a
// ~62-day window is slow. Widening this number is the cheap stopgap if that day comes first.
const JOB_PUNCH_LOOKBACK_DAYS = 31;

// --- HELPER FUNCTIONS FOR PAYROLL LOGIC (unchanged) ---
// compcut-8e2f4a17 — pay lives only in users/{id}/private/comp. Strip any copy off a main user
// record before it can reach getEmployeeWage (mirrors functions/utils/compPrivacy.withoutComp).
const withoutMainRecordPay = ({ hourlyRate, salaryWage, laborCostRate, ...rest }) => rest;

const getPeriodRange = (periodStart, frequency) => {
    const start = startOfDay(periodStart);
    const periodLength = frequency === 'bi-weekly' ? 13 : 6;
    const end = endOfWeek(addDays(start, periodLength - 6), { weekStartsOn: 0 });
    return { start, end };
};

const getCurrentPeriodStart = (settings) => {
    const today = startOfDay(new Date());
    const { startDate, frequency } = settings;
    const periodLengthDays = frequency === 'bi-weekly' ? 14 : 7;
    const daysSinceFirstStart = differenceInDays(today, startDate);
    if (daysSinceFirstStart < 0) return startDate;
    const periodsSinceStart = Math.floor(daysSinceFirstStart / periodLengthDays);
    return addDays(startDate, periodsSinceStart * periodLengthDays);
};

const safeToDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.toDate) return timestamp.toDate();
    return new Date(timestamp);
};

// --- CURRENCY FORMATTER ---
const formatMoney = (amount) =>
    amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// --- ORG PAYROLL DEFAULTS (fallback when org has no config) ---
const DEFAULT_ORG_PAYROLL_DEFAULTS = {
    schedulePreset: '5x8', workDaysPerWeek: 5, workHoursPerDay: 8,
    travelBufferMinutes: 30, shiftTrackingMode: 'estimate',
};

// Day name constants for working-day detection
const DAY_NAMES_ORDERED = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const PRESET_WORK_DAYS = {
    '5x8':  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    '4x10': ['monday', 'tuesday', 'wednesday', 'thursday'],
    '3x12': ['monday', 'wednesday', 'friday'],
};

// Returns effective schedule for an employee (personal override > org default)
const getEffectiveSchedule = (emp, orgDefaults) => {
    const d = orgDefaults || DEFAULT_ORG_PAYROLL_DEFAULTS;

    // Resolve which specific days count as working days
    const resolveWorkDays = (source, preset) => {
        if (source?.workDays && source.workDays.length > 0) return source.workDays;
        if (preset && PRESET_WORK_DAYS[preset]) return PRESET_WORK_DAYS[preset];
        return d.workDays || PRESET_WORK_DAYS['5x8'];
    };

    if (emp?.schedulePreset && emp.schedulePreset !== '') {
        return {
            workDaysPerWeek: Number(emp.workDaysPerWeek) || d.workDaysPerWeek,
            workHoursPerDay: Number(emp.workHoursPerDay) || d.workHoursPerDay,
            workDays: resolveWorkDays(emp, emp.schedulePreset),
        };
    }
    return {
        workDaysPerWeek: Number(d.workDaysPerWeek) || 5,
        workHoursPerDay: Number(d.workHoursPerDay) || 8,
        workDays: resolveWorkDays(d, d.schedulePreset),
    };
};

// Returns effective shift tracking mode (employee override > org default)
const getEffectiveTrackingMode = (emp, orgDefaults) => {
    if (emp?.shiftTrackingMode && emp.shiftTrackingMode !== '') return emp.shiftTrackingMode;
    return (orgDefaults || DEFAULT_ORG_PAYROLL_DEFAULTS).shiftTrackingMode || 'estimate';
};

// periodtot-3e-fn-9c2a4f10 — SINGLE SOURCE for the period's hours + approval totals, shared by the
// Timesheets-tab strip (periodtot-3e-strip) AND the Payroll Summary cards (periodtot-3e-cards) so the
// two tabs can NEVER show different numbers for what a manager is paying. Reproduces payRows' hours +
// inclusion EXACTLY — the salary/off scheduled-hours estimate (payRows 1220-1235), the `?? vs ||` paid
// rule (1216), and the active/salary/time-off inclusion filter (1285-1287) — but WITHOUT the gross-pay
// or time-off-DECISION coupling, so it can run on the Timesheets tab where PayrollSummaryTab is not
// mounted. Time-off-in-period here mirrors getTimeOffInPeriod (1164) but reads only emp.timeOffHistory
// + the range (no PayrollSummaryTab state). Hours only — gross stays the Payroll tab's job. If payRows'
// hours/inclusion rules ever change, change them HERE too.
const computePeriodTotals = (employees, timesheetData, dateRange, orgPayrollDefaults) => {
    const effectiveDefaults = orgPayrollDefaults || DEFAULT_ORG_PAYROLL_DEFAULTS;
    const rows = (employees || []).map(emp => {
        const data = timesheetData[emp.id];
        const isSalary = emp.compensationType === 'Salary';               // == getEmployeeWage type (1145)
        const empTrackingMode = getEffectiveTrackingMode(emp, effectiveDefaults);
        const empSchedule = getEffectiveSchedule(emp, effectiveDefaults);

        let totalShiftHours = data?.totalShiftHours || 0;
        let totalPaidHours = data?.totalPaidHours ?? totalShiftHours;     // ?? not || (1216)
        if ((isSalary || empTrackingMode === 'off') && !data) {           // estimate branch (1220 / 1228)
            const periodDays = dateRange.start && dateRange.end
                ? Math.round((dateRange.end - dateRange.start) / 86400000) + 1 : 14;
            const workDaysInPeriod = Math.round(periodDays * empSchedule.workDaysPerWeek / 7);
            totalShiftHours = workDaysInPeriod * empSchedule.workHoursPerDay;
            totalPaidHours = totalShiftHours;
        }
        // mirrors getTimeOffInPeriod (1164): any Approved time-off entry overlapping the range
        const hasTimeOff = (emp.timeOffHistory || []).some(entry => {
            if (entry.status !== 'Approved') return false;
            const startField = entry.startDate || entry.start;
            const endField = entry.endDate || entry.end;
            if (!startField || !endField || !dateRange.start || !dateRange.end) return false;
            try {
                const s = new Date(startField + 'T12:00:00');
                const e2 = new Date(endField + 'T12:00:00');
                return s <= dateRange.end && e2 >= dateRange.start;
            } catch { return false; }
        });
        return {
            totalShiftHours, totalPaidHours,
            isApproved: data?.status === 'Approved' || false,
            hasTimesheetData: !!data,
            isSalary,
            isActivePerson: isActiveAccount(emp),
            hasTimeOff,
        };
    }).filter(r => r.isActivePerson                                       // inclusion filter (1285-1287)
        ? (r.isSalary || r.hasTimesheetData || r.hasTimeOff)
        : (r.hasTimesheetData || r.hasTimeOff));

    return {
        totalShiftHours: rows.reduce((s, r) => s + r.totalShiftHours, 0),
        totalPaidHours: rows.reduce((s, r) => s + r.totalPaidHours, 0),
        approvedCount: rows.filter(r => r.isApproved).length,
        payableCount: rows.length,                                        // == payRows.length
    };
};

// actbreak-3e-fn — period-level activity breakdown of the SHIFT CLOCK for the Hours Reconciliation panel.
// Mirrors the day-header chip aggregation (DayRow, collapse-3e-agg) with ONE deliberate divergence: Work is
// an EXPLICIT bucket, not the implicit baseline — on a payroll screen the parts must visibly sum to the
// Shift (clock) total, so Work is shown. Built-ins resolve from activityType; a custom resolves from the
// SNAPSHOTTED activityName frozen on the shift at clock-in (never the live org map), so a rename never
// rewrites history. A legacy shift with no activityType folds into Work (activityType || 'work'), so no
// historical figure moves. Open shifts carry no `duration` (duration ?? 0 → 0), so the buckets sum to
// exactly totalShiftHours — this partitions the clock (one source), NOT the Non-job residual. Order is
// deterministic, never data order: Work first, then Travel/Break/Shop, then customs by descending hours,
// tie-break by name. Returns [] for a period with no shift rows.
const ACTBREAK_BUILTIN_LABEL = { work: 'Work', travel: 'Travel', break: 'Break', shop: 'At the shop' };
const ACTBREAK_BUILTIN_ORDER = { work: 0, travel: 1, break: 2, shop: 3 };
const computeActivityBreakdown = (dailyData) => {
    const agg = new Map();
    Object.values(dailyData || {}).forEach(day => {
        (day.rawShifts || []).forEach(s => {
            const t = s.activityType || 'work';
            const isBuiltin = t === 'work' || t === 'travel' || t === 'break' || t === 'shop';
            const label = isBuiltin ? ACTBREAK_BUILTIN_LABEL[t] : (s.activityName || 'Other');
            const key = isBuiltin ? t : `custom:${label}`;
            const cur = agg.get(key) || { key, label, hours: 0, paidHours: 0, isBuiltin, order: isBuiltin ? ACTBREAK_BUILTIN_ORDER[t] : 99 };
            cur.hours += (s.duration ?? 0);
            cur.paidHours += s.paid ? (s.duration ?? 0) : 0;
            agg.set(key, cur);
        });
    });
    return Array.from(agg.values()).sort((a, b) =>
        a.isBuiltin !== b.isBuiltin ? (a.isBuiltin ? -1 : 1)
        : a.isBuiltin ? a.order - b.order
        : (b.hours - a.hours) || a.label.localeCompare(b.label));
};

// Estimate shift times from job data + travel buffer. Returns {start, end, hours} or null.
const estimateShiftFromJobs = (dayData, bufferMinutes = 30) => {
    const jobs = dayData.jobs || [];
    if (jobs.length === 0) return null;
    // Try to derive first/last job time from the jobData.startAt timestamps
    let earliest = null, latest = null;
    jobs.forEach(job => {
        const raw = job.jobData?.startAt;
        if (!raw) return;
        const t = raw.toDate ? raw.toDate() : new Date(raw);
        if (!earliest || t < earliest) earliest = t;
        // Approximate end: start + clocked/allocated hours
        const hrs = parseFloat(job.clockedHours ?? job.hours) || 0;
        const end = new Date(t.getTime() + hrs * 3600000);
        if (!latest || end > latest) latest = end;
    });
    if (!earliest) return null;
    const bufMs = bufferMinutes * 60000;
    const start = new Date(earliest.getTime() - bufMs);
    const end   = new Date((latest || earliest).getTime() + bufMs);
    return { start, end, hours: Math.max(0, (end - start) / 3600000) };
};

// --- AVATAR COLOUR PALETTE ---
const AVATAR_COLORS = [
    { bg: 'bg-indigo-100', text: 'text-indigo-700', ring: 'ring-indigo-200' },
    { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
    { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-200' },
    { bg: 'bg-rose-100', text: 'text-rose-700', ring: 'ring-rose-200' },
    { bg: 'bg-sky-100', text: 'text-sky-700', ring: 'ring-sky-200' },
    { bg: 'bg-violet-100', text: 'text-violet-700', ring: 'ring-violet-200' },
];

const getAvatarColor = (index) => AVATAR_COLORS[index % AVATAR_COLORS.length];

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

// --- SUB-COMPONENTS ---

// c4e28d61 — ShiftSummary removed: the per-entry list in DayRow.shiftRows() now renders each punch
// individually, and the day span+total is a computed-min/max footer there. Its only caller is gone.

const JobDetailTable = ({ jobs, onJobSelect }) => {
    if (jobs.length === 0) return null;
    return (
        <div className="mt-3 rounded-lg border border-slate-100 overflow-hidden">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Job</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Sold</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actual</th>
                        <th className="w-6 px-2"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {jobs.map((job, index) => {
                        // jobday-6b2f9e14 — the over/under verdict is WHOLE ESTIMATE vs WHOLE JOB, carried
                        // only on the row that shows the estimate. It used to compare one number against
                        // `job.allocatedHours || 0`, which on a continuation row (allocatedHours null)
                        // would read as an estimate of ZERO and flag every such row red for going over.
                        const canJudge = job.jobTotalClockedHours !== null && job.jobTotalClockedHours !== undefined
                            && job.allocatedHours !== null && job.allocatedHours !== undefined;
                        const isOver = canJudge && job.jobTotalClockedHours > job.allocatedHours;
                        return (
                            <tr key={index} className="bg-white hover:bg-slate-50 transition-colors">
                                <td className="px-3 py-2.5">
                                    <button
                                        onClick={() => onJobSelect(job.jobData)}
                                        className="text-indigo-600 hover:text-indigo-800 font-medium hover:underline text-left leading-tight"
                                    >
                                        {job.jobTitle}
                                    </button>
                                    {job.address && (
                                        <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[200px]">{job.address}</p>
                                    )}
                                </td>
                                <td className="px-3 py-2.5 text-right text-slate-500 tabular-nums text-xs">
                                    {job.allocatedHours !== null && job.allocatedHours !== undefined
                                        ? `${parseFloat(job.allocatedHours).toFixed(2)} hrs`
                                        // jobday-6b2f9e14 — a bare dash reads as "estimated zero". On a
                                        // continuation row it means "the estimate is on another row", so say
                                        // which. dotted underline = there is something to hover.
                                        : job.isContinuation
                                            ? <span className="text-slate-300 cursor-help border-b border-dotted border-slate-300" title="The estimate is for the whole job, so it is shown once — on the first day this person worked it. This row shows only that day's actual hours.">—</span>
                                            : <span className="text-slate-300">—</span>
                                    }
                                </td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-xs">
                                    {job.clockedHours !== null && job.clockedHours !== undefined ? (
                                        <span className={`font-semibold ${isOver ? 'text-rose-600' : 'text-emerald-600'}`}>
                                            {parseFloat(job.clockedHours).toFixed(2)} hrs
                                        </span>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                    {/* jobday-6b2f9e14 — see the residual guard in the calc effect. */}
                                    {job.isResidual && (
                                        <span
                                            className="ml-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold align-middle cursor-help"
                                            title={`Unaccounted: ${parseFloat(job.residualHours ?? 0).toFixed(2)} hrs of this person's total on this job are not covered by any timer, so they are shown here on the job's scheduled day. The total still matches what the job was costed and billed at — but the day this time was worked is unknown. Open the job to check its timers.`}
                                        >
                                            <AlertTriangle size={9} /> unaccounted
                                        </span>
                                    )}
                                </td>
                                <td className="px-2 py-2.5 text-center">
                                    {canJudge && (
                                        isOver
                                            ? <TrendingUp size={13} className="text-rose-400 inline" />
                                            : <TrendingDown size={13} className="text-emerald-400 inline" />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// c2d5f0a9 — reason lists split by mode: creation reasons for 'add', correction reasons for 'edit'
const SHIFT_ADD_REASONS = [
    'Forgot to clock in', 'Forgot to clock out', 'Employee did not clock in',
    'System error', 'Other',
];
const SHIFT_EDIT_REASONS = [
    // 5f90a3d7 — 'Adjusting for break' removed: shrinking a shift to represent a lunch is the false
    // payroll record this seam eliminates; the correct fix is splitting the day into two punch pairs.
    'Correcting clock-in time', 'Correcting clock-out time', 'Wrong shift recorded',
    'System error', 'Other',
];
// a9f5027c — reasons for archiving (soft-deleting) a shift; the choice + optional note become archivedReason
const SHIFT_DELETE_REASONS = [
    'Duplicate shift', 'Accidental / test punch', 'Wrong employee', 'Recorded in error', 'Other',
];

const ShiftEditorForm = ({ mode, title, contextLabel, initialStart, initialEnd, onSave, onCancel, saving }) => {
    // b6c04e18 — mode is now 'add' | 'edit' ('add-out' merged into 'edit'); both fields always editable.
    // Use passed prefill verbatim — do NOT fall back to 08:00/17:00 here, or an intentionally empty
    // clock-out (dangling shift in edit mode) would be clobbered. DayRow supplies add-mode defaults.
    const [start, setStart] = useState(initialStart || '');
    const [end, setEnd] = useState(initialEnd || '');
    const reasonOptions = mode === 'add' ? SHIFT_ADD_REASONS : SHIFT_EDIT_REASONS;
    const [reason, setReason] = useState(reasonOptions[0]);

    const hrs = (() => {
        try {
            const [sh, sm] = start.split(':').map(Number);
            const [eh, em] = end.split(':').map(Number);
            const h = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
            return h > 0 ? h.toFixed(2) + 'h' : '—';
        } catch { return '—'; }
    })();

    return (
        <div className="mt-2 bg-white border border-slate-200 rounded-lg px-3 py-3 space-y-2">
            {title && <p className="text-sm font-semibold text-slate-700">{title}</p>}
            {contextLabel && <p className="text-xs text-slate-500 -mt-1">{contextLabel}</p>}
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className="block text-xs text-slate-500 mb-1">Clock in</label>
                    <input type="time" value={start} onChange={e => setStart(e.target.value)}
                        className="border border-slate-200 rounded px-2 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-indigo-300 outline-none" />
                </div>
                <div>
                    <label className="block text-xs text-slate-500 mb-1">Clock out</label>
                    <input type="time" value={end} onChange={e => setEnd(e.target.value)}
                        className="border border-slate-200 rounded px-2 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-indigo-300 outline-none" />
                </div>
                <span className="text-xs text-slate-400 pb-1.5">{hrs}</span>
            </div>
            <div>
                <label className="block text-xs text-slate-500 mb-1">{mode === 'add' ? 'Reason for manual entry' : 'Reason for correction'}</label>
                <select value={reason} onChange={e => setReason(e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white text-slate-700">
                    {reasonOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
            </div>
            <div className="flex gap-2 pt-1">
                <button onClick={() => onSave({ start, end, reason })}
                    disabled={saving}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded transition-colors disabled:opacity-50">
                    {saving ? 'Saving…' : mode === 'add' ? '+ Add Shift' : 'Save Changes'}
                </button>
                <button onClick={onCancel} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded transition-colors">
                    Cancel
                </button>
            </div>
        </div>
    );
};

const ShiftDeleteForm = ({ shift, dayLabel, onDelete, onCancel, deleting }) => {
    // a9f5027c — archive-not-destroy confirm: reason dropdown + optional note (same shape as the edit
    // reasons). Both go into archivedReason. Names the specific punch + day so the wrong row can't be
    // deleted blind. Deleting a payroll record should not be a single unguarded click.
    const [reason, setReason] = useState(SHIFT_DELETE_REASONS[0]);
    const [note, setNote] = useState('');
    const label = `${format(shift.shiftStart, 'h:mm a')} – ${shift.shiftEnd ? `${format(shift.shiftEnd, 'h:mm a')} (${(shift.duration ?? 0).toFixed(2)}h)` : 'no clock-out'}`;
    return (
        <div className="mt-2 bg-white border border-rose-200 rounded-lg px-3 py-3 space-y-2">
            <p className="text-sm font-semibold text-rose-700">Delete this shift?</p>
            <p className="text-xs text-slate-600">{label} on {dayLabel}. It will be archived (kept for records, recoverable by support), not permanently erased.</p>
            <div>
                <label className="block text-xs text-slate-500 mb-1">Reason</label>
                <select value={reason} onChange={e => setReason(e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white text-slate-700">
                    {SHIFT_DELETE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
            </div>
            <div>
                <label className="block text-xs text-slate-500 mb-1">Note (optional)</label>
                <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. duplicate of the morning punch"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-rose-300 outline-none" />
            </div>
            <div className="flex gap-2 pt-1">
                <button onClick={() => onDelete({ reason, note })} disabled={deleting}
                    className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded transition-colors disabled:opacity-50">
                    {deleting ? 'Deleting…' : 'Delete & archive'}
                </button>
                <button onClick={onCancel} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded transition-colors">
                    Cancel
                </button>
            </div>
        </div>
    );
};

// d4a1f0c7 — account-status helpers shared by the grid, the payroll tab, and EmployeeCard.
// The timesheet must show every ACTIVE person (empty rows flag missed punches) plus any
// NON-ACTIVE person who has recorded hours in the period, each with a plain-English label.
const isActiveAccount = (u) => u?.status === 'active' || u?.status === 'Active';
const employmentLabel = (status) =>
    (status === 'active' || status === 'Active') ? null
    : status === 'invited' ? 'Not yet started'
    : status === 'downgraded' ? 'Moved to team member'
    : 'No longer employed';

// abs0-7c4e9d21-k — leaveLabel: the approved-leave type covering this day ('Vacation' | 'Sick' |
// 'Personal' | a legacy fallback), or null. Computed once per day in EmployeeCard (block -m).
const DayRow = ({ dayKey, dayData, isEmpty, leaveLabel, onJobSelect, canApprove, userId, empName, trackingMode, travelBuffer, currentUserOrgId, currentUserId }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [editorMode, setEditorMode] = useState(null); // null | 'add' | 'edit'  (d1f27a53: 'add-out' merged into 'edit')
    const [editingShiftId, setEditingShiftId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');
    const [deletingShiftId, setDeletingShiftId] = useState(null); // a9f5027c — which row's delete panel is open
    const [deleting, setDeleting] = useState(false);

    const hasJobs = dayData.jobs.length > 0;
    const hasCompleteShift = dayData.rawShifts?.some(s => s.shiftStart && s.shiftEnd);
    const hasDanglingShift = (dayData.shiftStart && !dayData.shiftEnd) ||
        dayData.rawShifts?.some(s => s.shiftStart && !s.shiftEnd);
    const hasAnyShift = hasCompleteShift || hasDanglingShift || dayData.rawShifts?.length > 0;
    const totalJobHrs = dayData.jobs.reduce((sum, j) => sum + (parseFloat(j.hours) || 0), 0);
    // jobpunch-7d1a4e93 — Phase 1 display-only job timers for this user+day (see calc useEffect).
    // canExpand also opens a punch-only day (timer but no shift / no costed hours), so those
    // punches are never hidden — the "job punch exists but the person never clocked in" case.
    const jobPunches = dayData.jobPunches || [];
    const hasPunches = jobPunches.length > 0;
    // collapse-3e-expand — every day with shifts must expand now that the punch rows live behind the
    // disclosure; without this a shift-only day (no jobs/no timers) would have no way to reach its rows.
    const canExpand = hasAnyShift || hasJobs || hasPunches;
    // collapse-3e-forceopen — keep the rows visible while an edit/delete panel is open, so collapsing
    // mid-edit can't hide the row being changed (the panel still targets editingShiftId either way).
    const effectiveExpanded = isExpanded || (canApprove && (!!editorMode || !!deletingShiftId));
    // exception-3e-d-4b9e08 — Seam D: the day-level "needs review" set. Four conditions a manager must
    // catch; each (except strict-missing, which has no row) also marks its row on expand. Reasons feed
    // the header triangle's hover text. Single source: dayData.rawShifts, per the C→D contract.
    const dSelfReported = dayData.rawShifts?.some(s => s.shiftEndSelfReported);
    const dHasOverlap = dayData.rawShifts?.some(s => s._overlaps);
    const dMissingStrict = trackingMode === 'strict' && hasJobs && dayData.rawShifts?.length === 0 && !dayData.shiftStart;
    // jobday-6b2f9e14 — the residual guard's VISIBLE END. The guard deposits hours no timer
    // accounts for; this is what puts them in front of a person. It joins the day-header
    // triangle that already exists for the four shift exceptions — the same amber mark a
    // manager scans before approving, visible on the COLLAPSED row without expanding
    // anything, and it blocks nothing. Without this line the guard would only ever write a
    // flag into a data structure nobody opens, which is the failure mode it exists to avoid.
    const dUnaccountedJobHours = dayData.jobs?.some(j => j.isResidual);
    const exceptionReasons = [
        hasDanglingShift && 'Open shift — no clock-out',
        dMissingStrict && 'Missing shift (strict mode)',
        dSelfReported && 'Self-reported clock-out',
        dHasOverlap && 'Overlapping punches',
        dUnaccountedJobHours && 'Unaccounted job hours — costed time with no timer behind it',
    ].filter(Boolean);
    const needsReview = exceptionReasons.length > 0;

    const estimate = (!hasAnyShift && hasJobs && trackingMode === 'estimate')
        ? estimateShiftFromJobs(dayData, travelBuffer)
        : null;

    // collapse-3e-agg — collapse the day's punch rows into an activity breakdown for the header chips.
    // work/absent = the baseline and NEVER get a chip. Built-ins resolve from activityType; a custom
    // activity resolves from the SNAPSHOTTED activityName on the record (frozen at clock-in, exactly as
    // the mobile timesheet reads it) — never the live org definitions, so a rename never rewrites history.
    // A chip is "unpaid" only when it contributed ZERO paid hours that day (robust to a mid-period paid
    // change). Order is deterministic, never data order: built-ins first (Travel, Break, Shop), then
    // customs by descending hours, tie-break by name. Travel's destination stays in the expanded rows,
    // not the chip, so two travel legs merge cleanly.
    const BUILTIN_CHIP = { travel: 'Travel', break: 'Break', shop: 'At the shop' };
    const BUILTIN_CHIP_ORDER = { travel: 0, break: 1, shop: 2 };
    const activityChips = (() => {
        const rows = dayData.rawShifts || [];
        if (rows.length === 0) return [];
        const agg = new Map();
        rows.forEach(s => {
            const t = s.activityType || 'work';
            if (t === 'work') return; // baseline — no chip
            const isBuiltin = t === 'travel' || t === 'break' || t === 'shop';
            const label = isBuiltin ? BUILTIN_CHIP[t] : (s.activityName || 'Other');
            const key = isBuiltin ? t : `custom:${label}`;
            const cur = agg.get(key) || { key, label, hours: 0, paidHours: 0, isBuiltin, order: isBuiltin ? BUILTIN_CHIP_ORDER[t] : 99 };
            cur.hours += (s.duration ?? 0);
            cur.paidHours += s.paid ? (s.duration ?? 0) : 0;
            agg.set(key, cur);
        });
        return Array.from(agg.values()).sort((a, b) =>
            a.isBuiltin !== b.isBuiltin ? (a.isBuiltin ? -1 : 1)
            : a.isBuiltin ? a.order - b.order
            : (b.hours - a.hours) || a.label.localeCompare(b.label));
    })();

    // collapse-3e-headline — paid leads, clock muted only on a gap (Marcus's call). An all-paid day is a
    // single number. A day whose only shift is still open shows "on the clock now", not a bogus 0.00.
    const clockHrs = dayData.dailyShiftHours ?? 0;
    const paidHrs = dayData.dailyPaidHours ?? clockHrs;
    const hasPaidGap = (clockHrs - paidHrs) > 0.001;
    const onlyOpenShift = hasDanglingShift && clockHrs < 0.005;

    const saveShift = async (values) => {
        setSaving(true);
        setSavedMsg('');
        try {
            const base = new Date(dayKey + 'T00:00:00');
            const parseTime = (t) => {
                const [h, m] = t.split(':').map(Number);
                const d = new Date(base); d.setHours(h, m, 0, 0); return d;
            };
            // e1c740b2 — overlap guard: WARN (not block) when the resulting interval overlaps ANOTHER
            // shift doc for this user+day. Touching endpoints (12:00 out / 12:00 in) are a legal lunch
            // split, not an overlap (strict <). Excludes the doc being edited. Same-day multiplicity
            // itself is never flagged — only true time overlap.
            const overlapsOther = (start, end) => {
                if (!end) return null;
                return (dayData.rawShifts || []).find(s =>
                    s.id !== editingShiftId && s.shiftStart && s.shiftEnd &&
                    start < s.shiftEnd && s.shiftStart < end
                );
            };
            const confirmIfOverlap = (start, end) => { // b7f3e2a1 — warn (not block); name gross pay, not just hours
                const ov = overlapsOther(start, end);
                if (!ov) return true;
                return window.confirm(`This overlaps an existing shift ${format(ov.shiftStart, 'h:mm a')}–${format(ov.shiftEnd, 'h:mm a')} that day. Both count — this employee's hours and gross pay double-count for the overlap. Save anyway?`);
            };

            if (editorMode === 'add') {
                const shiftStart = parseTime(values.start);
                const shiftEnd = parseTime(values.end);
                // f4a91c2e — catch empty/malformed times up front so an Invalid Date never reaches
                // Timestamp.fromDate (which throws a cryptic "Failed to save"). Add mode requires both
                // times; empty-clock-out saves stay an edit-only (dangling-shift) capability.
                if (isNaN(shiftStart.getTime()) || isNaN(shiftEnd.getTime())) {
                    alert('Enter both a clock-in and a clock-out time to add a shift.');
                    setSaving(false); return;
                }
                if (shiftEnd <= shiftStart) { alert('Clock-out must be after clock-in.'); setSaving(false); return; }
                if (!confirmIfOverlap(shiftStart, shiftEnd)) { setSaving(false); return; } // e1c740b2
                await addDoc(collection(db, `users/${userId}/shifts`), {
                    shiftStart: Timestamp.fromDate(shiftStart),
                    shiftEnd: Timestamp.fromDate(shiftEnd),
                    organizationId: currentUserOrgId,
                    userId, userName: empName,
                    addedByAdmin: true,
                    editReason: values.reason,
                    addedBy: currentUserId,
                    addedAt: serverTimestamp(),
                });
                setSavedMsg(`Shift added: ${values.start} – ${values.end}`);
            } else if (editorMode === 'edit' && editingShiftId) {
                // 9a1e7b34 — unified edit path (former 'add-out' merged in). Both fields editable.
                const orig = dayData.rawShifts?.find(s => s.id === editingShiftId);
                const shiftStart = parseTime(values.start);
                const hasEnd = !!(values.end && values.end.trim() !== '');
                const shiftEnd = hasEnd ? parseTime(values.end) : null;
                const wasComplete = !!orig?.shiftEnd; // shift already had a clock-out

                if (hasEnd && shiftEnd <= shiftStart) { alert('Clock-out must be after clock-in.'); setSaving(false); return; }
                // A completed shift must keep a clock-out; only a still-open shift may be saved end-empty.
                if (!hasEnd && wasComplete) { alert('This shift already has a clock-out. Enter a clock-out time to save.'); setSaving(false); return; }
                if (hasEnd && !confirmIfOverlap(shiftStart, shiftEnd)) { setSaving(false); return; } // e1c740b2

                // Confirm gate: only when old and new both have an end (dangling→completed is an add, not an overwrite).
                // Gate on the LARGEST of start/end/duration deltas so a matched start+end move (zero duration
                // change) is still caught. Threshold 30 min.
                if (wasComplete && hasEnd) {
                    const dStartMs = Math.abs(shiftStart.getTime() - orig.shiftStart.getTime());
                    const dEndMs = Math.abs(shiftEnd.getTime() - orig.shiftEnd.getTime());
                    const oldDur = orig.duration;
                    const newDur = shiftDurationHours({ shiftStart, shiftEnd }); // 9f04d6a1 — same shared calc as the timesheet sum
                    const dDurMs = Math.abs(newDur - oldDur) * 3600000;
                    if (Math.max(dStartMs, dEndMs, dDurMs) >= 30 * 60000) {
                        const dayLabel = format(new Date(dayKey + 'T12:00:00'), 'EEE, MMM d');
                        const durChanged = Math.abs(newDur - oldDur) >= 0.01;
                        const msg = durChanged
                            ? `This will change ${dayLabel} from ${oldDur.toFixed(2)}h to ${newDur.toFixed(2)}h. Save this correction?`
                            : `This will change ${dayLabel} from ${format(orig.shiftStart, 'h:mm a')} - ${format(orig.shiftEnd, 'h:mm a')} to ${format(shiftStart, 'h:mm a')} - ${format(shiftEnd, 'h:mm a')}. Save this correction?`;
                        if (!window.confirm(msg)) { setSaving(false); return; }
                    }
                }

                const update = {
                    shiftStart: Timestamp.fromDate(shiftStart),
                    shiftEnd: hasEnd ? Timestamp.fromDate(shiftEnd) : null,
                    editReason: values.reason,
                    editedByAdmin: true,
                    editedBy: currentUserId,
                    editedAt: serverTimestamp(),
                    // Append, never overwrite. arrayUnion (not spread) because orig.editHistory rides a live
                    // onSnapshot; a read-modify-write could drop a concurrent entry. serverTimestamp() is
                    // illegal inside array elements, so history rows stamp editedAt with Timestamp.now().
                    editHistory: arrayUnion({
                        previousStart: orig?.shiftStart ? Timestamp.fromDate(orig.shiftStart) : null,
                        previousEnd: orig?.shiftEnd ? Timestamp.fromDate(orig.shiftEnd) : null,
                        newStart: Timestamp.fromDate(shiftStart),
                        newEnd: hasEnd ? Timestamp.fromDate(shiftEnd) : null,
                        editedBy: currentUserId,
                        editedAt: Timestamp.now(),
                        reason: values.reason,
                    }),
                };
                // Non-lossy originals: write only when absent, so they hold the TRUE first-recorded values.
                if (!orig?.originalStart && orig?.shiftStart) update.originalStart = Timestamp.fromDate(orig.shiftStart);
                if (!orig?.originalEnd && orig?.shiftEnd) update.originalEnd = Timestamp.fromDate(orig.shiftEnd);

                await updateDoc(doc(db, `users/${userId}/shifts`, editingShiftId), update);
                setSavedMsg(hasEnd ? `Shift updated: ${values.start} – ${values.end}` : `Clock-in updated: ${values.start}`);
            }
            setEditorMode(null);
            setEditingShiftId(null);
        } catch (err) {
            console.error('Shift save error:', err);
            alert('Failed to save. Please try again.');
        }
        setSaving(false);
    };

    // a9f5027c — archive-not-hard-delete. Re-read the FULL doc (rawShifts is only a projection), copy it
    // verbatim to organizations/{orgId}/shiftArchive/{sameId} with audit fields, then delete the original —
    // both writes in ONE batch so it can't half-complete. Shape matches functions/scripts/fixAllanJul17Shifts.js.
    const handleDeleteShift = async ({ reason, note }) => {
        if (!deletingShiftId) return;
        setDeleting(true);
        try {
            const shiftRef = doc(db, `users/${userId}/shifts`, deletingShiftId);
            const snap = await getDoc(shiftRef);
            if (!snap.exists()) { alert('This shift no longer exists.'); setDeleting(false); setDeletingShiftId(null); return; }
            const archivedReason = note && note.trim() ? `${reason} — ${note.trim()}` : reason;
            const batch = writeBatch(db);
            batch.set(doc(db, `organizations/${currentUserOrgId}/shiftArchive`, deletingShiftId), {
                ...snap.data(),
                archivedAt: serverTimestamp(),
                archivedBy: currentUserId,
                archivedReason,
                originalPath: `users/${userId}/shifts/${deletingShiftId}`,
            });
            batch.delete(shiftRef);
            await batch.commit();
            setDeletingShiftId(null);
            setSavedMsg('Shift archived and removed.');
        } catch (err) {
            console.error('Shift delete error:', err);
            alert('Failed to delete. Please try again.');
        }
        setDeleting(false);
    };

    // Shift status pill
    const daySummary = () => {
        if (dayData.rawShifts?.length > 0) {
            // collapse-3e-summary — the day's ANSWER: span · paid headline · activity chips. The punch
            // rows moved to shiftRows() in the expanded body; there is no footer — this IS the day total.
            return (
                <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-slate-600">
                        {dayData.shiftStart ? format(dayData.shiftStart, 'h:mm a') : '?'}
                        <span className="text-slate-400 mx-1">–</span>
                        {dayData.shiftEnd
                            ? format(dayData.shiftEnd, 'h:mm a')
                            : <span className="text-amber-600 font-semibold">Ongoing</span>}
                    </span>
                    <span className="text-slate-300">·</span>
                    {onlyOpenShift ? (
                        <span className="text-sm text-slate-400 italic">on the clock now</span>
                    ) : hasPaidGap ? (
                        <span className="text-sm font-semibold text-slate-700">
                            {paidHrs.toFixed(2)} hrs paid
                            <span className="font-normal text-slate-400"> · {clockHrs.toFixed(2)} on the clock</span>
                        </span>
                    ) : (
                        <span className="text-sm font-semibold text-slate-700">{clockHrs.toFixed(2)} hrs</span>
                    )}
                    {activityChips.length > 0 && (
                        <span className="flex flex-wrap items-center gap-1">
                            {activityChips.map(c => {
                                const unpaid = c.paidHours < 0.005;
                                return (
                                    <span key={c.key}
                                        className={`text-xs px-1.5 py-0.5 rounded border ${unpaid ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-violet-50 text-violet-700 border-violet-200'}`}
                                        title={`${c.label} — ${c.hours.toFixed(2)}h${unpaid ? ', unpaid' : ''}`}>
                                        {c.label} {c.hours.toFixed(2)}h{unpaid ? ' · unpaid' : ''}
                                    </span>
                                );
                            })}
                        </span>
                    )}
                </div>
            );
        }
        // abs0-7c4e9d21-l — approved leave outranks BOTH no-shift readings below. Position is the point:
        // ABOVE the estimate branch, so a leave day that also has a job booked on it can't be read as an
        // amber job-derived estimate; BELOW the `rawShifts?.length > 0` branch, so a real punch still wins —
        // someone genuinely can come in on their vacation. Because this returns early, the fallback below
        // and its "+ Add Shift" button are unreachable for a leave day, which is the whole fix: the screen
        // stops inviting the office to fabricate a punch for a day someone was legitimately away.
        // No hours are shown, because none exist yet — inventing them is the dishonesty this seam removes.
        // ☀ is the glyph the time-off reviewer already uses at :1735, so no new icon import is needed.
        // The expanded shift editor is deliberately still reachable.
        if (leaveLabel) {
            return (
                <div className="flex items-center gap-1.5 text-slate-500">
                    <span className="text-slate-400 text-xs flex-shrink-0">☀</span>
                    <span className="text-xs font-medium">{leaveLabel} · no shift expected</span>
                </div>
            );
        }
        if (estimate) {
            return (
                // b8d3e05f — this day has NO shift document; the times are a job-derived estimate, not a
                // live open shift. Copy + button now say so, so it isn't mistaken for a dangling shift.
                <div className="flex items-center gap-1.5 text-amber-600">
                    <AlertCircle size={13} className="flex-shrink-0" />
                    <span className="text-xs font-medium">
                        No shift recorded
                        <span className="ml-1 text-amber-400">· est. {format(estimate.start, 'h:mm a')} – {format(estimate.end, 'h:mm a')} from jobs ({estimate.hours.toFixed(2)}h)</span>
                    </span>
                    {canApprove && (
                        <button onClick={e => { e.stopPropagation(); setEditorMode(editorMode === 'add' ? null : 'add'); }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 px-1.5 py-0.5 rounded bg-indigo-50 hover:bg-indigo-100 transition-colors font-medium ml-1">
                            + Add Shift
                        </button>
                    )}
                </div>
            );
        }
        return (
            <div className="flex items-center gap-1.5 text-amber-600">
                <AlertCircle size={13} className="flex-shrink-0" />
                <span className="text-xs font-medium">No shift recorded</span>
                {canApprove && trackingMode !== 'off' && (
                    <button onClick={e => { e.stopPropagation(); setEditorMode(editorMode === 'add' ? null : 'add'); }}
                        className="text-xs text-indigo-600 hover:text-indigo-800 px-1.5 py-0.5 rounded bg-indigo-50 hover:bg-indigo-100 transition-colors font-medium ml-1">
                        + Add Shift
                    </button>
                )}
            </div>
        );
    };

    // collapse-3e-rows — the punch rows relocated VERBATIM from the old header (interior unchanged): one
    // editable row per shift doc + the multi-day "+ Add shift" control. The multi-punch footer that used
    // to sit here is gone — daySummary() states the day total and paid gap once. Rendered in the expanded body.
    const shiftRows = () => (
        <div className="min-w-0 space-y-1">
                    {dayData.rawShifts.map(shift => {
                        const isEditingThis = editorMode === 'edit' && editingShiftId === shift.id;
                        return (
                            <div key={shift.id} className="flex items-center gap-2">
                                <Clock size={13} className={`flex-shrink-0 ${shift.shiftEnd ? 'text-slate-400' : 'text-amber-400'}`} />
                                {/* rowexc-3e-d-2d6a5f — Seam D: same marker on the offending row so expanding
                                    lands on it. Open / self-reported / overlapping rows earn it (both members
                                    of an overlap pair carry _overlaps). Strict-missing has no row, so it's
                                    header-only. Added after the Clock icon, not replacing it. */}
                                {(!shift.shiftEnd || shift.shiftEndSelfReported || shift._overlaps) && (
                                    <AlertTriangle size={13} className="flex-shrink-0 text-amber-600" title={[
                                        !shift.shiftEnd && 'Open shift — no clock-out',
                                        shift.shiftEndSelfReported && 'Self-reported clock-out',
                                        shift._overlaps && 'Overlapping punch',
                                    ].filter(Boolean).join(' · ')} />
                                )}
                                {/* shiftlabel-w-4a7c9e21 — activity chip, leading the row so exceptions form a
                                    left-gutter colour stripe when scanning a week. Absent/unknown/work resolve to
                                    no chip, so legacy + web-written rows read exactly as today (clock icon then
                                    times). Only break/travel/shop get a label; travel appends its destination when
                                    the writer stamped one. max-w + truncate keeps a long label off the time column. */}
                                {(() => {
                                    const t = shift.activityType || 'work';
                                    if (t === 'work') return null;
                                    const text = t === 'travel'
                                        ? (shift.travelToLabel ? `Travel — ${shift.travelToLabel}` : 'Travel')
                                        : t === 'break' ? 'Break'
                                        : t === 'shop' ? 'At the shop'
                                        : null;
                                    if (!text) return null;
                                    return (
                                        <span className="flex-shrink-0 max-w-[10rem] truncate text-xs px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200" title={text}>
                                            {text}
                                        </span>
                                    );
                                })()}
                                <span className="text-sm font-medium text-slate-600">
                                    {format(shift.shiftStart, 'h:mm a')}
                                    <span className="text-slate-400 mx-1">to</span>
                                    {shift.shiftEnd
                                        ? format(shift.shiftEnd, 'h:mm a')
                                        : <span className="text-amber-600 font-semibold">No clock-out</span>}
                                </span>
                                {shift.shiftEnd && (
                                    <span className="text-slate-400 text-xs">({(shift.duration ?? 0).toFixed(2)} hrs)</span>
                                )}
                                {/* paidhrs-4j1 — frozen-flag marker; only an explicit unpaid shift shows it, so the gap is traceable to the row that caused it. */}
                                {shift.paid === false && (
                                    <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200" title="This activity is set to unpaid — its hours are on the clock but not in gross pay.">
                                        Unpaid
                                    </span>
                                )}
                                {/* 3f8b2d6e — provenance badges. Self-reported first, then edited, so the row reads
                                    chronologically: an employee-stated end that a manager later corrected shows BOTH,
                                    which is the sequence most worth a second look before payroll approval. */}
                                {shift.shiftEndSelfReported && (
                                    <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Clock-out time was stated by the employee, not recorded by the clock.">
                                        Self-reported
                                    </span>
                                )}
                                {shift.editedByAdmin && (
                                    <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200" title="This punch was corrected by an admin.">
                                        Edited
                                    </span>
                                )}
                                {canApprove && (<>
                                    <button onClick={e => { e.stopPropagation();
                                        setDeletingShiftId(null); // a9f5027c — panels are mutually exclusive
                                        if (isEditingThis) { setEditorMode(null); setEditingShiftId(null); }
                                        else { setEditingShiftId(shift.id); setEditorMode('edit'); }
                                    }} className="flex-shrink-0 text-xs text-slate-400 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 transition-colors" title="Edit shift">
                                        ✏
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); // a9f5027c
                                        setEditorMode(null); setEditingShiftId(null);
                                        setDeletingShiftId(deletingShiftId === shift.id ? null : shift.id);
                                    }} className="flex-shrink-0 text-xs text-slate-400 hover:text-rose-600 px-1.5 py-0.5 rounded hover:bg-rose-50 transition-colors" title="Delete shift">
                                        🗑
                                    </button>
                                </>)}
                            </div>
                        );
                    })}
                    {canApprove && (
                        // 3d6b8e14 — add a second (or third) punch pair to a day that already has shifts
                        <button onClick={e => { e.stopPropagation(); setDeletingShiftId(null); setEditingShiftId(null); setEditorMode(editorMode === 'add' ? null : 'add'); }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 px-1.5 py-0.5 rounded bg-indigo-50 hover:bg-indigo-100 transition-colors font-medium">
                            + Add shift
                        </button>
                    )}
        </div>
    );

    // d1f27a53 — editor prefill/title/context. 'add' keeps the estimate-or-default fallback; 'edit'
    // prefills from the shift's OWN stored punch times via the same date-fns format() the row displays
    // with, so prefilled values match the visible row exactly (no partial timezone fix here).
    const editingShift = dayData.rawShifts?.find(s => s.id === editingShiftId);
    // 3d6b8e14 — ADD-editor seeding. On a day that already has shifts (the lunch-split case), seed the
    // new pair's start from the day's last punch-out and leave the end BLANK for the owner to fill — the
    // 08:00/17:00 (or estimate) default would overlap the existing shift. A no-shift day keeps the old
    // estimate-or-08:00/17:00 seed.
    const addingToExistingDay = dayData.rawShifts?.length > 0;
    const editorInitialStart = addingToExistingDay
        ? (dayData.shiftEnd ? format(dayData.shiftEnd, 'HH:mm') : '')
        : (estimate ? format(estimate.start, 'HH:mm') : '08:00');
    const editorInitialEnd = addingToExistingDay
        ? ''
        : (estimate ? format(estimate.end, 'HH:mm') : '17:00');
    const editInitialStart = editingShift?.shiftStart ? format(editingShift.shiftStart, 'HH:mm') : '';
    const editInitialEnd = editingShift?.shiftEnd ? format(editingShift.shiftEnd, 'HH:mm') : '';
    const editorTitle = editorMode === 'add'
        ? 'Add a shift'
        : (editingShift?.shiftEnd ? 'Edit recorded shift' : 'Add clock-out / fix shift');
    const editorContext = (editorMode === 'edit' && editingShift)
        ? `Currently recorded: ${format(editingShift.shiftStart, 'h:mm a')} – ${editingShift.shiftEnd ? `${format(editingShift.shiftEnd, 'h:mm a')} (${(editingShift.duration ?? 0).toFixed(2)}h)` : '(no clock-out)'}`
        : undefined;

    // abs0-7c4e9d21-p — a leave day IS isEmpty, but it is not a problem to be flagged, so it loses the
    // amber tint. Comment sits above `return (` because a JSX comment cannot stand beside a root element.
    return (
        <div className={`rounded-lg border overflow-hidden ${leaveLabel ? 'border-slate-200 bg-slate-50/60' : isEmpty ? 'border-amber-100 bg-amber-50/40' : 'border-slate-100'}`}>
            {/* b83c6f04 — header is a div-with-role, NOT a <button>, so the per-row edit/delete
                buttons inside shiftRows() aren't nested in a <button> (invalid DOM + event bubbling).
                Keyboard toggle (Enter/Space) + role/tabIndex keep it accessible, only when hasJobs. */}
            <div
                /* jobpunch-1b6f2c05 — open the row for punch-only days too, not just job days */
                role={canExpand ? 'button' : undefined}
                tabIndex={canExpand ? 0 : undefined}
                aria-expanded={canExpand ? isExpanded : undefined}
                onClick={() => canExpand && setIsExpanded(e => !e)}
                onKeyDown={e => { if (canExpand && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setIsExpanded(v => !v); } }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                    ${canExpand ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}
                    ${isExpanded ? 'bg-slate-50' : isEmpty ? 'bg-transparent' : 'bg-white'}
                `}
            >
                <div className="w-28 flex-shrink-0">
                    <p className="text-sm font-semibold text-slate-700">
                        {format(new Date(dayKey + 'T12:00:00'), 'EEE, MMM d')}
                    </p>
                </div>
                <div className="flex-1 min-w-0">
                    {/* collapse-3e-header-call — the collapsed answer; the punch rows moved to shiftRows() below */}
                    {daySummary()}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    {hasJobs && (
                        <div className="flex items-center gap-1.5 text-slate-500">
                            <Briefcase size={12} className="text-slate-400" />
                            <span className="text-xs font-medium">{dayData.jobs.length} {dayData.jobs.length === 1 ? 'job' : 'jobs'}</span>
                            <span className="text-slate-300">·</span>
                            <span className="text-xs font-semibold text-slate-600">{totalJobHrs.toFixed(2)} hrs</span>
                        </div>
                    )}
                    {/* exception-3e-d-4b9e08 — Seam D swaps the old status dots for one warning triangle +
                        "Needs review" label. Label always on (scan without hovering); hover lists every
                        reason. flex-shrink-0 keeps it one line — the flex-1 summary column absorbs the width
                        by wrapping chips one line sooner on the problem day only. Add `hidden lg:inline` to
                        the label span if a busy header wraps awkwardly on narrower screens. */}
                    {needsReview && (
                        <span className="flex items-center gap-1 text-amber-600 flex-shrink-0" title={exceptionReasons.join(' · ')}>
                            <AlertTriangle size={14} className="flex-shrink-0" />
                            <span className="text-xs font-semibold">Needs review</span>
                        </span>
                    )}
                    {/* collapse-3e-punchhint — punch-count hint removed: every day with content is now
                        chevron-expandable, so the count was redundant. Timers remain in the expanded body. */}
                    {canExpand && (
                        <ChevronDown size={15} className={`text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    )}
                </div>
            </div>

            {/* Inline shift editor */}
            {editorMode && canApprove && (
                <div className="px-4 pb-3 bg-slate-50 border-t border-slate-100">
                    <ShiftEditorForm
                        key={`${editorMode}-${editingShiftId || 'new'}`}
                        mode={editorMode}
                        title={editorTitle}
                        contextLabel={editorContext}
                        initialStart={editorMode === 'edit' ? editInitialStart : editorInitialStart}
                        initialEnd={editorMode === 'edit' ? editInitialEnd : editorInitialEnd}
                        onSave={saveShift}
                        onCancel={() => { setEditorMode(null); setEditingShiftId(null); }}
                        saving={saving}
                    />
                </div>
            )}

            {/* a9f5027c — archive/delete confirm panel (mutually exclusive with the editor above) */}
            {deletingShiftId && canApprove && (() => {
                const s = dayData.rawShifts?.find(x => x.id === deletingShiftId);
                if (!s) return null;
                return (
                    <div className="px-4 pb-3 bg-rose-50 border-t border-rose-100">
                        <ShiftDeleteForm
                            shift={s}
                            dayLabel={format(new Date(dayKey + 'T12:00:00'), 'EEE, MMM d')}
                            onDelete={handleDeleteShift}
                            onCancel={() => setDeletingShiftId(null)}
                            deleting={deleting}
                        />
                    </div>
                );
            })()}

            {savedMsg && (
                <div className="px-4 py-2 bg-emerald-50 border-t border-emerald-100">
                    {/* 3c8e5f92 — live onSnapshot recomputes hours automatically; old "Refresh" copy was wrong */}
                    <p className="text-xs text-emerald-700 font-medium">✓ {savedMsg} · Hours update automatically.</p>
                </div>
            )}

            {effectiveExpanded && canExpand && (
                <div className="px-4 pb-4 bg-white border-t border-slate-100">
                    {/* collapse-3e-body — the punch rows now open here, above the job detail. */}
                    {dayData.rawShifts?.length > 0 && (
                        <div className="mb-3">
                            {shiftRows()}
                        </div>
                    )}
                    {hasJobs && <JobDetailTable jobs={dayData.jobs} onJobSelect={onJobSelect} />}
                    {/* jobpunch-5e0c7a26 — Phase 1 display-only job-timer list. Explicitly labelled
                        NOT payroll: the shift span above is the pay figure; job time must never read
                        as additive. Open punch shows "running…"; times shown as-is (no overlap flag). */}
                    {hasPunches && (
                        <div className={hasJobs ? 'mt-4 pt-3 border-t border-slate-100' : ''}>
                            <p className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1.5">
                                <Clock size={12} className="text-slate-400" />
                                Job timers — for reference, not counted in payroll
                            </p>
                            <div className="space-y-1">
                                {jobPunches.map((p, i) => (
                                    <div key={`${p.jobId}-${i}`} className="flex items-center gap-2 text-xs text-slate-600">
                                        {/* jobid-3e-render-4f2b8a — Seam B: identify by CUSTOMER + address (how the
                                            office thinks), NOT the title — a recurring job's title repeats on every
                                            line and identifies nothing. Customer leads: a short name survives truncation
                                            where a long address gets cut. Cascade customer → address → title so a
                                            customerless/internal job still leads with something real, never a blank or
                                            dash; the address suffix shows only when a customer led. One line, widened
                                            (flex-1 min-w-0 truncate) so the id gets room and the times stay pinned right
                                            (times are NOT clickable). Title is still in the job-hours table above. Quiet
                                            at rest; link affordance only on hover/focus. Click reuses onJobSelect(jobData)
                                            (206), gated (a deleted job yields no timer line — built from live timePunches). */}
                                        {(() => {
                                            const primary = p.clientName || p.address || p.jobTitle;
                                            const secondary = p.clientName && p.address ? p.address : null;
                                            return (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); if (p.jobData) onJobSelect(p.jobData); }}
                                                    disabled={!p.jobData}
                                                    title={`${p.jobNumber ? `#${p.jobNumber} · ` : ''}${primary}${secondary ? ` — ${secondary}` : ''}`}
                                                    className="flex-1 min-w-0 truncate text-left text-slate-500 hover:text-indigo-600 hover:underline focus:text-indigo-600 focus:underline outline-none cursor-pointer disabled:cursor-default disabled:hover:text-slate-500 disabled:hover:no-underline"
                                                >
                                                    {p.jobNumber ? <span className="text-slate-400">#{p.jobNumber} </span> : null}{primary}{secondary ? <span className="text-slate-400"> — {secondary}</span> : null}
                                                </button>
                                            );
                                        })()}
                                        {/* jobday-6b2f9e14 — a voided or capped punch keeps its real times but
                                            counts zero hours. Struck through + labelled so the line cannot be
                                            read as time that counted; the times stay visible because that is
                                            the whole point of voiding rather than deleting. */}
                                        {p.voided && (
                                            <span className="flex-shrink-0 rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 text-[10px] font-semibold cursor-help"
                                                title="Voided by an admin — the times are kept as a record, but these hours count as zero on this job.">
                                                voided
                                            </span>
                                        )}
                                        {!p.voided && p.capped && (
                                            <span className="flex-shrink-0 rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold cursor-help"
                                                title="This timer ran past 13 hours, so it was closed when the job was finished but not costed — nobody observed the end. It counts as zero hours until someone corrects it on the job.">
                                                capped — not counted
                                            </span>
                                        )}
                                        <span className={`font-medium ${p.voided ? 'text-slate-400 line-through' : 'text-slate-600'}`}>
                                            {format(p.punchIn, 'h:mm a')}
                                            <span className={p.voided ? 'mx-1' : 'text-slate-400 mx-1'}>to</span>
                                            {p.punchOut
                                                ? format(p.punchOut, 'h:mm a')
                                                : <span className="text-emerald-600 font-semibold">running…</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const EmployeeCard = ({ userId, data, colorIndex, canApprove, dateRange, onApproveTimesheet, onJobSelect, empData, orgPayrollDefaults, currentUserProfile }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const color = getAvatarColor(colorIndex);
    const isApproved = data.status === 'Approved';

    const trackingMode = getEffectiveTrackingMode(empData, orgPayrollDefaults);
    const travelBuffer = Number(orgPayrollDefaults?.travelBufferMinutes ?? 30);
    const empSchedule = getEffectiveSchedule(empData, orgPayrollDefaults);

    // Build the full list of days to show: all expected working days in the period,
    // merged with any days that have actual activity (shifts/jobs).
    const allDisplayDays = React.useMemo(() => {
        const workDaySet = new Set(empSchedule.workDays || []);
        const activityDays = new Set(Object.keys(data.dailyData || {}));

        // Union of scheduled days + days with actual activity
        let daySet = new Set(activityDays);
        // 4c8a1e70 — a departed/not-started person only shows days they actually worked; don't
        // pad their card with empty "expected" scheduled days.
        if (trackingMode !== 'off' && isActiveAccount({ status: data.userStatus }) && dateRange?.start && dateRange?.end) {
            eachDayOfInterval({ start: dateRange.start, end: dateRange.end }).forEach(day => {
                const dayName = DAY_NAMES_ORDERED[day.getDay()];
                if (workDaySet.has(dayName)) daySet.add(format(day, 'yyyy-MM-dd'));
            });
        }

        return Array.from(daySet)
            .sort((a, b) => new Date(a) - new Date(b))
            .map(dayKey => ({
                dayKey,
                dayData: data.dailyData?.[dayKey] || {
                    dayDisplay: format(new Date(dayKey + 'T12:00:00'), 'EEEE, MMM d'),
                    shiftStart: null, shiftEnd: null, rawShifts: [], jobs: [], dailyShiftHours: 0,
                },
                isEmpty: !data.dailyData?.[dayKey],
                // abs0-7c4e9d21-m — NAME the leave, don't just say a shift wasn't expected. `type` is already
                // on the entry ('Vacation' | 'Sick' | 'Personal', bare <option> values from
                // EmployeeProfileModal.js:1230 / TeamMemberModal.js:1180-1182) and all 6 production records
                // carry a readable one, so this needs no absence-hours model and no type registry.
                // empData is already a prop (read at :1055/:1057) and is the MERGED record — resolveProfileRead
                // folds users/{id}/private/profile in at :2267-2269 — so timeOffHistory is present with no new
                // Firestore read. It can be undefined (it comes from a .find), hence ?.
                // Same Approved gate, same startDate||start fallback and same string compare as block -g:
                // the two surfaces MUST agree on which days are leave days.
                leaveLabel: (empData?.timeOffHistory || []).reduce((found, e) => {
                    if (found || e.status !== 'Approved') return found;
                    const s = e.startDate || e.start, en = e.endDate || e.end;
                    return (s && en && dayKey >= s && dayKey <= en) ? (e.type || 'Time off') : found;
                }, null),
            }));
    }, [data.dailyData, data.userStatus, empSchedule.workDays, trackingMode, dateRange, empData?.timeOffHistory]); // 4c8a1e70 — userStatus gates schedule padding; abs0-7c4e9d21-n — absence feeds leaveLabel, or the label goes stale when an absence is added or removed

    // Gate: block approve if any day has dangling shift or (strict mode) missing shift
    const hasDangling = allDisplayDays.some(({ dayData: d }) =>
        (d.shiftStart && !d.shiftEnd) || d.rawShifts?.some(s => s.shiftStart && !s.shiftEnd)
    );
    const hasMissingStrict = trackingMode === 'strict' &&
        allDisplayDays.some(({ dayData: d }) => d.jobs.length > 0 && d.rawShifts?.length === 0 && !d.shiftStart);
    // s9selfapprove-8d1f4b26 — a Manager can't approve their OWN week (firestore.rules refuses it
    // too); an Owner or Admin signs it off.
    const isOwnWeekForManager = mustAskOwnerForOwnTime(currentUserProfile, userId);
    const approveBlocked = isOwnWeekForManager || hasDangling || hasMissingStrict;
    const approveBlockReason = isOwnWeekForManager
        ? 'An Owner or Admin approves your own timesheet'
        : hasDangling
        ? 'Fix open shifts (missing clock-outs) before approving'
        : 'Strict mode: all missing shifts must be added before approving';

    return (
        <div className={`rounded-xl border transition-shadow duration-200 overflow-hidden
            ${isApproved ? 'border-emerald-200' : 'border-slate-200'} shadow-sm bg-white
        `}>
            {/* Summary bar */}
            <button
                onClick={() => setIsExpanded(e => !e)}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
            >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ring-2 ${color.bg} ${color.text} ${color.ring}`}>
                    {getInitials(data.name)}
                </div>

                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-base leading-tight">{data.name}</p>
                    {employmentLabel(data.userStatus) && (
                        <span className="inline-block mt-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{employmentLabel(data.userStatus)}</span>
                    )}{/* e9b2f4c6 */}
                    <div className="mt-0.5">
                        {isApproved ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                                <CheckCircle2 size={10} /> Approved
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" /> Pending
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-5 flex-shrink-0">
                    <div className="text-center hidden sm:block">
                        <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide leading-none mb-1">Shift</p>
                        <p className="text-lg font-bold text-slate-700 tabular-nums leading-none">
                            {data.totalShiftHours.toFixed(2)}<span className="text-xs font-normal text-slate-400 ml-0.5">h</span>
                        </p>
                        {/* paidhrs-4l1 — paid sub-line only on a gap */}
                        {(data.totalShiftHours - (data.totalPaidHours ?? data.totalShiftHours)) > 0.001 && (
                            <p className="text-xs font-medium text-amber-600 tabular-nums leading-none mt-0.5">{(data.totalPaidHours ?? data.totalShiftHours).toFixed(2)}h paid</p>
                        )}
                    </div>
                    <div className="w-px h-8 bg-slate-100 hidden sm:block" />
                    <div className="text-center hidden sm:block">
                        <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide leading-none mb-1">Jobs</p>
                        <p className="text-lg font-bold text-slate-700 tabular-nums leading-none">
                            {data.totalJobHours.toFixed(2)}<span className="text-xs font-normal text-slate-400 ml-0.5">h</span>
                        </p>
                    </div>
                    <div className="w-px h-8 bg-slate-100 hidden sm:block" />
                    <div className="text-center hidden sm:block">
                        <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide leading-none mb-1">Efficiency</p>
                        <p className="text-lg font-bold text-slate-700 tabular-nums leading-none">
                            {data.totalShiftHours > 0
                                ? `${((data.totalJobHours / data.totalShiftHours) * 100).toFixed(0)}%`
                                : <span className="text-slate-400 text-base">N/A</span>
                            }
                        </p>
                    </div>
                    <ChevronDown
                        size={18}
                        className={`text-slate-400 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                    />
                </div>
            </button>

            {/* Expanded section */}
            {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-4 space-y-2">
                    {allDisplayDays.length > 0 ? (
                        allDisplayDays.map(({ dayKey, dayData, isEmpty, leaveLabel }) => ( /* abs0-7c4e9d21-o */
                            <DayRow
                                key={dayKey}
                                dayKey={dayKey}
                                dayData={dayData}
                                isEmpty={isEmpty}
                                leaveLabel={leaveLabel}
                                onJobSelect={onJobSelect}
                                canApprove={canApprove}
                                userId={userId}
                                empName={data.name}
                                trackingMode={trackingMode}
                                travelBuffer={travelBuffer}
                                currentUserOrgId={currentUserProfile?.organizationId}
                                currentUserId={currentUserProfile?.id || currentUserProfile?.uid}
                            />
                        ))
                    ) : (
                        <p className="text-sm text-slate-400 text-center py-6">No activity recorded this period.</p>
                    )}

                    <div className="flex justify-end pt-3 mt-1 border-t border-slate-200">
                        {isApproved ? (
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-lg border border-emerald-200">
                                <CheckCircle2 size={15} /> Timesheet Approved
                            </div>
                        ) : (
                            canApprove && (
                                approveBlocked ? (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-amber-600 font-medium">{approveBlockReason}</span>
                                        <button disabled className="px-5 py-2 bg-slate-200 text-slate-400 text-sm font-semibold rounded-lg cursor-not-allowed">
                                            Approve Timesheet
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => onApproveTimesheet({
                                            userId,
                                            userName: data.name,
                                            weekStart: dateRange.start,
                                            weekEnd: dateRange.end,
                                            totalHours: data.totalShiftHours,
                                            paidHours: data.totalPaidHours, // paidhrs-4i1 — freeze both; == totalHours until unpaid time exists
                                            dailyData: data.dailyData,
                                        })}
                                        className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
                                    >
                                        Approve Timesheet
                                    </button>
                                )
                            )
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- STAT HOLIDAY LOOKUP TABLE (Canada, province-aware) ---
// Dates calculated for current and adjacent years. Province codes match CompanySettings `state` field.
const getStatHolidays = (province, year) => {
    // Helper: nth weekday of a month
    const nthDay = (y, m, weekday, n) => {
        const first = new Date(y, m, 1);
        const offset = (weekday - first.getDay() + 7) % 7;
        return new Date(y, m, 1 + offset + (n - 1) * 7);
    };
    // Helper: Easter Sunday (Anonymous Gregorian algorithm)
    const easter = (y) => {
        const a = y % 19, b = Math.floor(y / 100), c = y % 100;
        const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4), k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m2 = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m2 + 114) / 31) - 1;
        const day = ((h + l - 7 * m2 + 114) % 31) + 1;
        return new Date(y, month, day);
    };
    const easterSunday = easter(year);
    const goodFriday = new Date(easterSunday); goodFriday.setDate(goodFriday.getDate() - 2);
    const easterMonday = new Date(easterSunday); easterMonday.setDate(easterMonday.getDate() + 1);

    // Universal Canadian stats (all provinces)
    const universal = [
        { date: new Date(year, 0, 1),  name: "New Year's Day" },
        { date: goodFriday,             name: "Good Friday" },
        { date: new Date(year, 6, 1),  name: "Canada Day" },
        { date: nthDay(year, 8, 1, 1), name: "Labour Day" },      // 1st Mon Sept
        { date: new Date(year, 11, 25), name: "Christmas Day" },
    ];

    // Province-specific additions
    const provinceCode = (province || '').toUpperCase().trim();
    // Normalize common full names to codes
    const codeMap = { 'ONTARIO': 'ON', 'BRITISH COLUMBIA': 'BC', 'ALBERTA': 'AB',
        'QUEBEC': 'QC', 'MANITOBA': 'MB', 'SASKATCHEWAN': 'SK',
        'NOVA SCOTIA': 'NS', 'NEW BRUNSWICK': 'NB', 'NEWFOUNDLAND': 'NL',
        'PRINCE EDWARD ISLAND': 'PE' };
    const prov = codeMap[provinceCode] || provinceCode;

    const extra = [];

    if (['ON', 'MB', 'SK', 'AB', 'BC', 'PE', 'NS', 'NB', 'NL', 'QC'].includes(prov)) {
        // Victoria Day: last Mon before May 25
        const vic = new Date(year, 4, 25);
        vic.setDate(vic.getDate() - ((vic.getDay() + 6) % 7 || 7));
        extra.push({ date: vic, name: 'Victoria Day' });
    }
    if (['ON', 'AB', 'MB', 'SK', 'BC', 'PE', 'NB', 'NL', 'NS'].includes(prov)) {
        extra.push({ date: nthDay(year, 9, 1, 2), name: 'Thanksgiving Day' }); // 2nd Mon Oct
    }
    if (['ON', 'AB', 'MB', 'SK', 'BC', 'PE', 'NS', 'NB', 'NL'].includes(prov)) {
        extra.push({ date: new Date(year, 11, 26), name: 'Boxing Day' });
    }
    if (['ON', 'AB', 'SK', 'MB', 'BC', 'PE', 'NS', 'NB', 'NL'].includes(prov)) {
        // Family Day / Islander Day / Heritage Day / Louis Riel Day — 3rd Mon Feb
        const famNames = { 'ON': 'Family Day', 'AB': 'Family Day', 'SK': 'Family Day',
            'MB': 'Louis Riel Day', 'BC': 'Family Day', 'PE': "Islander Day",
            'NS': 'Heritage Day', 'NB': 'Family Day', 'NL': 'Family Day' };
        extra.push({ date: nthDay(year, 1, 1, 3), name: famNames[prov] || 'Family Day' });
    }
    if (prov === 'QC') {
        extra.push({ date: easterMonday, name: 'Easter Monday' });
        extra.push({ date: new Date(year, 5, 24), name: 'Fête nationale (St. Jean Baptiste)' });
    }
    if (['BC', 'MB', 'ON'].includes(prov)) {
        // Civic Holiday / BC Day / Terry Fox Day — 1st Mon Aug (optional in ON, included as advisory)
        const civNames = { 'BC': 'BC Day', 'MB': 'Terry Fox Day', 'ON': 'Civic Holiday (optional)' };
        extra.push({ date: nthDay(year, 7, 1, 1), name: civNames[prov] });
    }
    if (['BC', 'MB', 'YT'].includes(prov)) {
        extra.push({ date: new Date(year, 8, 30), name: 'National Day for Truth & Reconciliation' });
    }

    return [...universal, ...extra].sort((a, b) => a.date - b.date);
};

// Get stat holidays that fall within a date range, returns array of {date, name}
const getStatsInPeriod = (province, start, end) => {
    if (!start || !end || !province) return [];
    const years = [start.getFullYear()];
    if (end.getFullYear() !== start.getFullYear()) years.push(end.getFullYear());
    const holidays = years.flatMap(y => getStatHolidays(province, y));
    return holidays.filter(h => h.date >= start && h.date <= end);
};

// --- PAYROLL SUMMARY TAB ---
const PayrollSummaryTab = ({ timesheetData, employees, dateRange, payrollSettings, currentUserProfile, paidRun, onMarkAsPaid, orgProvince, orgPayrollDefaults }) => {

    const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);
    // timeOffDecisions: { [userId_date]: 'paid' | 'unpaid' | null }
    const [timeOffDecisions, setTimeOffDecisions] = useState({});
    // statDecisions: { [statDateStr]: 'regular' | 'worked' | 'na' }
    const [statDecisions, setStatDecisions] = useState({});
    // addShiftDraft: { [userId_dayKey]: { start: 'HH:mm', end: 'HH:mm', saving: bool, saved: bool } }
    const [addShiftDraft, setAddShiftDraft] = useState({});

    // --- PAYROLL DRAFT PERSISTENCE ---
    const orgId = currentUserProfile?.organizationId;
    const periodDraftKey = dateRange.start ? format(dateRange.start, 'yyyy-MM-dd') : null;

    // Load saved decisions for this period on mount / period change
    useEffect(() => {
        if (!orgId || !periodDraftKey) return;
        setTimeOffDecisions({});
        setStatDecisions({});
        const loadDraft = async () => {
            try {
                const draftRef = doc(db, `organizations/${orgId}/payrollDrafts`, periodDraftKey);
                const snap = await getDoc(draftRef);
                if (snap.exists()) {
                    const d = snap.data();
                    if (d.timeOffDecisions) setTimeOffDecisions(d.timeOffDecisions);
                    if (d.statDecisions) setStatDecisions(d.statDecisions);
                }
            } catch (err) {
                console.error('Failed to load payroll draft:', err);
            }
        };
        loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, periodDraftKey]);

    // Manually save time off decisions to Firestore (called by the Save button in the absences box)
    const [timeOffSaving, setTimeOffSaving] = React.useState(false);
    const [timeOffSaved, setTimeOffSaved] = React.useState(false);

    const saveTimeOffDecisions = async () => {
        if (!orgId || !periodDraftKey) return;
        setTimeOffSaving(true);
        setTimeOffSaved(false);
        try {
            const draftRef = doc(db, `organizations/${orgId}/payrollDrafts`, periodDraftKey);
            await setDoc(draftRef, {
                timeOffDecisions,
                updatedAt: serverTimestamp(),
            }, { merge: true });
            setTimeOffSaved(true);
            setTimeout(() => setTimeOffSaved(false), 3000);
        } catch (err) {
            console.error('Failed to save time off decisions:', err);
            alert('Failed to save. Please try again.');
        } finally {
            setTimeOffSaving(false);
        }
    };

    const setTimeOffDecisionAndPersist = (key, value) => {
        setTimeOffDecisions(prev => ({ ...prev, [key]: value }));
        setTimeOffSaved(false); // mark as unsaved when changed
    };

    // abs0-7c4e9d21-q — statDecisions was LOADED at :1344 and never written by anything, so every holiday
    // decision was lost on reload, on tab switch (this component unmounts) and on period change (the load
    // effect resets to {} on every periodDraftKey change) — and the CSV's "Stat Holiday Notes" column read
    // "Not reviewed" for every holiday on any fresh page load.
    //
    // The fix is NOT "add statDecisions to saveTimeOffDecisions". The only save control is the Save
    // Absences button at :1795-1806, which lives INSIDE {hasPendingTimeOff && …} at :1731 — so a period
    // with a statutory holiday but no time off has no save control anywhere on the page. It goes in the
    // setter instead, which is what the name has always claimed.
    //
    // `next` is computed explicitly rather than read back from state: setStatDecisions is asynchronous, so
    // writing `statDecisions` here would persist the PREVIOUS map. saveTimeOffDecisions is deliberately
    // left alone — one write path per concern, so a stale map from that button can't overwrite a fresher
    // one. The alert matches saveTimeOffDecisions' existing failure behaviour; a console.error alone would
    // let the decision vanish on the next reload with nobody watching for it.
    const setStatDecisionAndPersist = async (key, value) => {
        const next = { ...statDecisions, [key]: value };
        setStatDecisions(next);
        if (!orgId || !periodDraftKey) return;
        try {
            const draftRef = doc(db, `organizations/${orgId}/payrollDrafts`, periodDraftKey);
            await setDoc(draftRef, { statDecisions: next, updatedAt: serverTimestamp() }, { merge: true });
        } catch (err) {
            console.error('Failed to save stat holiday decision:', err);
            alert('Could not save that holiday decision. Please try again.');
        }
    };

    const handleAddShift = async (userId, userName, dayKey) => {
        const draftKey = `${userId}_${dayKey}`;
        const draft = addShiftDraft[draftKey] || {};
        // Use stored values or fall back to the displayed defaults so the button always works
        const startVal = draft.start || '08:00';
        const endVal = draft.end || '17:00';

        // Build JS Date objects from dayKey + time strings
        const [startH, startM] = startVal.split(':').map(Number);
        const [endH, endM] = endVal.split(':').map(Number);
        const baseDate = new Date(dayKey + 'T00:00:00');
        const shiftStart = new Date(baseDate); shiftStart.setHours(startH, startM, 0, 0);
        const shiftEnd = new Date(baseDate); shiftEnd.setHours(endH, endM, 0, 0);

        if (shiftEnd <= shiftStart) {
            alert('End time must be after start time.');
            return;
        }

        setAddShiftDraft(p => ({ ...p, [draftKey]: { ...p[draftKey], saving: true } }));
        try {
            await addDoc(collection(db, `users/${userId}/shifts`), {
                shiftStart: Timestamp.fromDate(shiftStart),
                shiftEnd: Timestamp.fromDate(shiftEnd),
                organizationId: currentUserProfile.organizationId,
                userId,
                userName,
                addedByAdmin: true,
                addedBy: currentUserProfile.id || currentUserProfile.uid,
            });
            setAddShiftDraft(p => ({ ...p, [draftKey]: { ...p[draftKey], saving: false, saved: true } }));
        } catch (err) {
            console.error('Error adding shift:', err);
            alert('Failed to save shift. Please try again.');
            setAddShiftDraft(p => ({ ...p, [draftKey]: { ...p[draftKey], saving: false } }));
        }
    };

    // --- GROSS PAY CALCULATION ---
    const periodsPerYear = payrollSettings?.frequency === 'bi-weekly' ? 26 : 52;

    const getEmployeeWage = (userId) => {
        const emp = employees.find(e => e.id === userId);
        if (!emp) return { type: 'Hourly', rate: 0, display: 'No rate set' };
        if (emp.compensationType === 'Salary') {
            const annualSalary = parseFloat(emp.salaryWage) || 0;
            const perPeriod = annualSalary / periodsPerYear;
            return { type: 'Salary', annualSalary, perPeriod, display: `$${annualSalary.toLocaleString()}/yr` };
        }
        const rate = parseFloat(emp.hourlyRate) || 0;
        return { type: 'Hourly', rate, display: `$${formatMoney(rate)}/hr` };
    };

    // PHASE 1 FIX: Payroll uses SHIFT hours (full clocked day), not job hours.
    // Job hours are for costing, not compensation.
    // abs0-7c4e9d21-b — timeOffAdjustment is gone with getUnpaidDeduction, and the parameter is renamed
    // to what the single call site has always passed (totalPaidHours, not shift hours). No floor is added:
    // with nothing subtracted the expression cannot go negative from absence. The one remaining route to a
    // negative figure is a shift document whose end precedes its start (shiftDurationHours is signed by
    // design — see src/utils/shiftTime.js:40-47). That is a corrupt record, not a payroll rule; it is
    // pre-existing, this seam neither creates nor fixes it, and a clamp here would HIDE it, not fix it.
    const getGrossPay = (userId, totalPaidHours) => {
        const wage = getEmployeeWage(userId);
        if (wage.type === 'Salary') return wage.perPeriod; // flat regardless of hours
        return wage.rate * totalPaidHours;
    };

    // --- TIME OFF IN PERIOD (Phase 2) ---
    // For each employee, find approved time off entries that overlap this pay period
    const getTimeOffInPeriod = (userId) => {
        const emp = employees.find(e => e.id === userId);
        if (!emp?.timeOffHistory) return [];
        return (emp.timeOffHistory || []).filter(entry => {
            if (entry.status !== 'Approved') return false;
            try {
                // EmployeeProfileModal saves as startDate/endDate
                const startField = entry.startDate || entry.start;
                const endField = entry.endDate || entry.end;
                if (!startField || !endField) return false;
                const s = new Date(startField + 'T12:00:00');
                const e2 = new Date(endField + 'T12:00:00');
                return s <= dateRange.end && e2 >= dateRange.start;
            } catch { return false; }
        });
    };

    // abs0-7c4e9d21-a — getUnpaidDeduction DELETED, not clamped. It returned days × 8 and getGrossPay
    // subtracted it with NO floor, so gross could go negative. A Math.max(0, …) would have been the wrong
    // fix: an absence produces no shift document, so its hours are never inside totalPaidHours and there
    // is nothing to subtract from — any deduction removed hours the person ACTUALLY WORKED. A Fri→Mon
    // unpaid absence took 32h off the days they did work. Three further faults died with it: 8h was
    // hardcoded while empSchedule.workHoursPerDay is in scope and used at :1512/:1520 (a 4×10 employee's
    // day was deducted at 8h); it counted CALENDAR days; and it recomputed `days` while ignoring
    // entry.days, the field the writer stored. Zero production incidence — 0 payrollDrafts across all
    // 30 orgs, so no 'unpaid' decision has ever existed and this never executed against live data.
    // Paid leave becomes payable when an absence record carries HOURS. That is a later seam, not this one.

    // --- STAT HOLIDAYS IN PERIOD (Phase 3) ---
    const statsInPeriod = getStatsInPeriod(orgProvince, dateRange.start, dateRange.end);

    // --- BUILD PAY ROWS — from ALL active employees (salaried always appear) ---
    const effectiveDefaults = orgPayrollDefaults || DEFAULT_ORG_PAYROLL_DEFAULTS;
    const payRows = employees.map(emp => {
        const userId = emp.id;
        const data = timesheetData[userId];
        const wage = getEmployeeWage(userId);
        const empTrackingMode = getEffectiveTrackingMode(emp, effectiveDefaults);
        const empSchedule = getEffectiveSchedule(emp, effectiveDefaults);
        const travelBuf = Number(effectiveDefaults.travelBufferMinutes ?? 30);

        // For salaried employees or tracking=off: use scheduled hours for display
        let totalShiftHours = data?.totalShiftHours || 0;
        let totalPaidHours = data?.totalPaidHours ?? totalShiftHours; // paidhrs-4f1 — MUST be ?? not ||: a legit 0 (all shifts unpaid) is preserved; fallback fires only when there is no timesheet data, and then mirrors clock
        let totalJobHours = data?.totalJobHours || 0;
        let isEstimated = false;

        if (wage.type === 'Salary' && !data) {
            // Salaried with no timesheet data — show expected hours based on schedule
            const periodDays = dateRange.start && dateRange.end
                ? Math.round((dateRange.end - dateRange.start) / 86400000) + 1 : 14;
            const workDaysInPeriod = Math.round(periodDays * empSchedule.workDaysPerWeek / 7);
            totalShiftHours = workDaysInPeriod * empSchedule.workHoursPerDay;
            totalPaidHours = totalShiftHours; // paidhrs-4f2 — estimated hours are all paid; salary gross is flat anyway
            isEstimated = false; // salary is flat, hours are just informational
        } else if (empTrackingMode === 'off' && !data) {
            // Off mode: use full scheduled hours
            const periodDays = dateRange.start && dateRange.end
                ? Math.round((dateRange.end - dateRange.start) / 86400000) + 1 : 14;
            const workDaysInPeriod = Math.round(periodDays * empSchedule.workDaysPerWeek / 7);
            totalShiftHours = workDaysInPeriod * empSchedule.workHoursPerDay;
            totalPaidHours = totalShiftHours; // paidhrs-4f3 — estimated scheduled hours are all paid
        }

        const grossPay = getGrossPay(userId, totalPaidHours); // paidhrs-4f4 + abs0-7c4e9d21-c — paid-hours × rate; the unpaid-absence deduction is gone
        const isApproved = data?.status === 'Approved' || false;
        const timeOffEntries = getTimeOffInPeriod(userId);

        // Detect ALL expected working days in the period that have no shift recorded.
        // We enumerate every calendar day in the range, check it against the employee's
        // work schedule (specific days of week), then flag any expected day that has
        // no shift punch — regardless of whether a job exists that day or not.
        const missingShiftDays = (() => {
            if (!isActiveAccount(emp)) return []; // 2d7a9c61 — a departed/not-started person isn't expected to clock in
            if (empTrackingMode === 'off') return []; // off mode never shows missing days
            if (!dateRange.start || !dateRange.end) return [];
            const workDaySet = new Set(empSchedule.workDays || []);
            const allPeriodDays = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
            return allPeriodDays
                .filter(day => {
                    const dayName = DAY_NAMES_ORDERED[day.getDay()];
                    if (!workDaySet.has(dayName)) return false; // not a scheduled day
                    const dayKey = format(day, 'yyyy-MM-dd');
                    // abs0-7c4e9d21-g — a day covered by approved leave is NOT a missing punch. Without this,
                    // approved leave put the person in the red "Missing shifts detected" panel beside an
                    // "+ Add" button, inviting the office to fabricate a shift for a day they were legitimately
                    // away. timeOffEntries is already in scope (assigned at :1527, six lines above this IIFE).
                    // Keys are 'YYYY-MM-DD' so string compare IS chronological — same idiom as :1767/:1996.
                    // Deliberately not eachDayOfInterval: that throws RangeError when end < start and would
                    // enumerate unbounded days for a malformed entry. Here a malformed entry matches nothing
                    // and the day falls through to today's behaviour — fails closed. Mirror of block -m.
                    const onLeave = timeOffEntries.some(e => {
                        const s = e.startDate || e.start, en = e.endDate || e.end;
                        return s && en && dayKey >= s && dayKey <= en;
                    });
                    if (onLeave) return false;
                    const dayData = data?.dailyData?.[dayKey];
                    // Flag if no shift recorded (dailyShiftHours === 0 and no shiftStart)
                    if (!dayData) return true; // no activity at all
                    return dayData.dailyShiftHours === 0 && !dayData.shiftStart;
                })
                .map(day => format(day, 'yyyy-MM-dd'));
        })();

        const allJobs = [];
        if (data) {
            Object.entries(data.dailyData).sort(([a], [b]) => new Date(a) - new Date(b)).forEach(([dayKey, dayData]) => {
                dayData.jobs.forEach(job => allJobs.push({ ...job, dayKey }));
            });
        }

        // actbreak-3e-compute — period activity breakdown of the shift clock (Work + Travel + Break + Shop +
        // customs) for the Hours Reconciliation panel below. Reuses the shift rows already in dailyData; []
        // when there is no timesheet data (the panel is hourly-only and never renders for those rows anyway).
        const activityBreakdown = data ? computeActivityBreakdown(data.dailyData) : [];

        return {
            userId, name: emp.name || data?.name || 'Unknown',
            wage, grossPay, isApproved,
            totalJobHours, totalShiftHours, totalPaidHours, isEstimated, // paidhrs-4f5
            activityBreakdown, // actbreak-3e-return — period per-activity clock breakdown for the reconciliation panel
            timeOffEntries, missingShiftDays, allJobs, // abs0-7c4e9d21-d — unpaidDeduction removed; nothing computes it any more
            empSchedule, empTrackingMode,
            hasTimesheetData: !!data,
            userStatus: emp.status || null,          // b8f34e0a — account status for the payroll label
            isActivePerson: isActiveAccount(emp),    // b8f34e0a — drives the inclusion rule below
        };
    // c4e18b52 — Active: unchanged rule (salaried always appear, so an empty row flags a missed
    // punch). Non-active: appear ONLY with real hours/time-off in the period — a salaried
    // departed employee does NOT get a phantom scheduled-hours row.
    }).filter(r => r.isActivePerson
        ? (r.wage.type === 'Salary' || r.hasTimesheetData || r.timeOffEntries.length > 0)
        : (r.hasTimesheetData || r.timeOffEntries.length > 0));

    // --- SUMMARY TOTALS ---
    const totalGross = payRows.reduce((sum, r) => sum + r.grossPay, 0);
    // periodtot-3e-cards-9c2a4f10 — hours + approval now come from the SHARED single source, the SAME
    // function the Timesheets-tab strip reads, so the two tabs can never disagree on what's being paid.
    // payRows still drives gross above, plus the per-employee table + CSV; its per-row totalShiftHours
    // uses identical logic, so this card total still equals the sum of the rows shown below.
    const { totalShiftHours: totalHours, totalPaidHours, approvedCount } = computePeriodTotals(employees, timesheetData, dateRange, orgPayrollDefaults);
    const allApproved = approvedCount === payRows.length && payRows.length > 0;
    const hasMissingShifts = payRows.some(r => r.missingShiftDays.length > 0);
    // abs0-7c4e9d21-h — was `e.start`, a field NO writer produces. Both modals persist `startDate`
    // (EmployeeProfileModal.js:482, TeamMemberModal.js:640 — `start` is only their local FORM field name),
    // and all 6 production records carry startDate while 0 carry a bare `start`. So the key was always
    // "{uid}_undefined", !undefined === true, and the banner could never clear. This now builds the same
    // key the reviewer WRITES at :1747, and keeps the `|| e.start` fallback the other six sites use.
    const hasPendingTimeOff = payRows.some(r =>
        r.timeOffEntries.some(e => !timeOffDecisions[`${r.userId}_${e.startDate || e.start}`])
    );
    // abs0b-3f7a2c91-a — TWO different questions that had been sharing one flag.
    // "Is there anything to review?" (does the period contain any absence at all) decides whether the
    // review PANEL renders. "Is anything still undecided?" decides whether we WARN, and now only tints.
    // Gating the panel on the second made the panel destroy itself: deciding the last entry flipped
    // hasPendingTimeOff false, unmounted the panel, and took the Save button with it BEFORE the decision
    // could be persisted — so a period could be driven to exactly one remaining unconfirmed absence and
    // no further, permanently. That was latent all along; abs0-7c4e9d21-h only unmasked it, because the
    // `e.start` typo had pinned hasPendingTimeOff true and kept the panel permanently on screen.
    //
    // THE RULE, because three other controls in this file look identical and are fine: this shape only
    // bites when a control mutates its own render gate through UNPERSISTED local state. handleAddShift,
    // handleMarkAsPaid and the DayRow editor all change their gates too, but only via Firestore data that
    // moves after a successful write — persistence first, gate flip second. Here the gate read React
    // state the buttons had just set, so it flipped before anything reached Firestore.
    //
    // This mirrors the stat-holiday panel below, which already renders on existence and tints on
    // pending. Not named `hasTimeOff`: that is taken at :134 (computePeriodTotals) and :1928 (row local).
    const hasAnyTimeOff = payRows.some(r => r.timeOffEntries.length > 0);
    const hasPendingStats = statsInPeriod.some(s => !statDecisions[format(s.date, 'yyyy-MM-dd')]);

    // --- CSV EXPORT ---
    const handleExport = () => {
        if (payRows.length === 0) { alert('No payroll data to export.'); return; }
        const periodLabel = dateRange.start && dateRange.end
            ? `${format(dateRange.start, 'yyyy-MM-dd')}_to_${format(dateRange.end, 'yyyy-MM-dd')}` : 'period';

        const statFlags = statsInPeriod.map(s => {
            const key = format(s.date, 'yyyy-MM-dd');
            const dec = statDecisions[key];
            // abs0-7c4e9d21-r — 'na' is an explicit N/A click (:1715) that already clears the warning at
            // :1594, but it printed as "Not reviewed", identical to never having looked at the holiday.
            return `${s.name} (${format(s.date, 'MMM d')}): ${dec === 'worked' ? 'Worked - premium pay applies' : dec === 'regular' ? 'Day off - holiday pay' : dec === 'na' ? 'Not applicable' : 'Not reviewed'}`;
        }).join(' | ');

        const headers = ['Employee', 'Pay Type', 'Shift Hours', 'Job Hours', 'Rate / Salary', 'Gross Pay',
            'Time Off', 'Unpaid Deduction (hrs)', 'Approval Status', 'Stat Holiday Notes', 'Paid Hours']; // paidhrs-4h1 — appended at END; moves no existing column
        const rows = payRows.map(r => [
            r.name,
            r.wage.type,
            r.wage.type === 'Hourly' ? r.totalShiftHours.toFixed(2) : 'N/A (Salaried)',
            r.totalJobHours.toFixed(2),
            r.wage.type === 'Salary' ? `$${r.wage.annualSalary.toLocaleString()} annually` : `$${formatMoney(r.wage.rate)}/hr`,
            `$${formatMoney(r.grossPay)}`,
            r.timeOffEntries.map(e => `${e.type} ${e.startDate || e.start}→${e.endDate || e.end}`).join('; ') || 'None',
            '0', // abs0-7c4e9d21-e — header kept so the 11-column file stays shape-identical (decision D2); there is no deduction any more, so this is always 0. The column retires with the per-pay-code export in Seam 7.
            r.isApproved ? 'Approved' : 'Pending',
            statFlags || 'None',
            r.wage.type === 'Hourly' ? r.totalPaidHours.toFixed(2) : 'N/A (Salaried)', // paidhrs-4h1 — matches Shift Hours format; == Shift Hours until a break is unpaid
        ]);

        // s9csv-4e1b7c28 — shared csvCell: formula guard + quote escaping (employee names and
        // time-off labels are typed text).
        const csvContent = [headers, ...rows]
            .map(row => row.map(csvCell).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Payroll_${periodLabel}.csv`;
        document.body.appendChild(link); link.click();
        document.body.removeChild(link); URL.revokeObjectURL(url);
    };

    if (payRows.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <DollarSign size={22} className="text-slate-400" />
                </div>
                <p className="text-slate-600 font-medium">No timesheet data for this period</p>
                <p className="text-sm text-slate-400 mt-1">Switch to the Timesheets tab to review and approve employee time.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">

            {/* DISCLAIMER */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                <strong>Gross pay only.</strong> Tax withholding, deductions, and remittances are calculated in your payroll software (Gusto, ADP, QuickBooks Payroll, etc.).
            </div>

            {/* ── PHASE 1: MISSING SHIFT NOTICE (fix in Timesheets tab) ── */}
            {hasMissingShifts && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
                    <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-800">Missing shifts detected</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                            {payRows.filter(r => r.missingShiftDays.length > 0).map(r =>
                                `${r.name} (${r.missingShiftDays.map(d => format(new Date(d + 'T12:00:00'), 'MMM d')).join(', ')})`
                            ).join(' · ')}
                        </p>
                        <p className="text-xs text-amber-500 mt-1">
                            Switch to the <strong>Timesheets tab</strong> to add or edit shifts for these employees before running payroll.
                        </p>
                    </div>
                </div>
            )}

            {/* ── PHASE 3: STAT HOLIDAY REVIEW ── */}
            {statsInPeriod.length > 0 && (
                <div className={`rounded-xl border px-4 py-4 ${hasPendingStats ? 'bg-violet-50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${hasPendingStats ? 'bg-violet-100' : 'bg-slate-100'}`}>
                            <span className={`text-base ${hasPendingStats ? 'text-violet-600' : 'text-slate-400'}`}>🗓</span>
                        </div>
                        <div className="flex-1">
                            <p className={`text-sm font-semibold mb-1 ${hasPendingStats ? 'text-violet-800' : 'text-slate-600'}`}>
                                Statutory holiday{statsInPeriod.length > 1 ? 's' : ''} in this period
                                {orgProvince ? ` · ${orgProvince}` : ''}
                            </p>
                            <p className="text-xs text-slate-500 mb-3">
                                Confirm each holiday's status. "Day off (paid)" exports a note for your payroll software to apply holiday pay.
                                "Worked — premium pay" flags the day for 1.5× calculation in your payroll software.
                            </p>
                            <div className="space-y-2">
                                {statsInPeriod.map(stat => {
                                    const key = format(stat.date, 'yyyy-MM-dd');
                                    const dec = statDecisions[key];
                                    return (
                                        <div key={key} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2.5 shadow-sm">
                                            <div className="flex-1 min-w-0">
                                                <span className="text-sm font-semibold text-slate-700">{stat.name}</span>
                                                <span className="text-xs text-slate-400 ml-2">{format(stat.date, 'EEEE, MMM d')}</span>
                                            </div>
                                            <div className="flex gap-1.5">
                                                <button
                                                    onClick={() => setStatDecisionAndPersist(key, 'regular')}
                                                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dec === 'regular' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'}`}
                                                >
                                                    ✓ Day off (paid)
                                                </button>
                                                <button
                                                    onClick={() => setStatDecisionAndPersist(key, 'worked')}
                                                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dec === 'worked' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-amber-50 hover:text-amber-700'}`}
                                                >
                                                    Worked — premium pay
                                                </button>
                                                <button
                                                    onClick={() => setStatDecisionAndPersist(key, 'na')}
                                                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dec === 'na' ? 'bg-slate-400 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                                                >
                                                    N/A
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── PHASE 2: TIME OFF REVIEW ── */}
            {/* abs0b-3f7a2c91-b — renders whenever the period HAS an absence, not only while one is still
                undecided. This conditional previously hid the whole review surface the moment the last
                decision was made: the entry list, both decision buttons per entry, the "✓ Saved"
                indicator, and the only Save path absences have. Pending state moves to the TINT, exactly
                as the stat-holiday panel does — sky while something is outstanding, slate once every
                absence is confirmed — so a settled period still shows what it decided and can change it.
                The settled panel is NOT inert: the entry rows are white cards, each decided entry keeps a
                saturated emerald/rose chip, and Save stays solid sky — only the prose dims. Tinting the
                Save button is the one thing that would make this look disabled, so it is left alone.
                The pre-export warning at :2213 is deliberately untouched and still keys on pending. */}
            {hasAnyTimeOff && (
                <div className={`rounded-xl border px-4 py-4 ${hasPendingTimeOff ? 'bg-sky-50 border-sky-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${hasPendingTimeOff ? 'bg-sky-100' : 'bg-slate-100'}`}>
                            <span className={`text-base ${hasPendingTimeOff ? 'text-sky-600' : 'text-slate-400'}`}>☀</span>
                        </div>
                        <div className="flex-1">
                            <p className={`text-sm font-semibold mb-1 ${hasPendingTimeOff ? 'text-sky-800' : 'text-slate-600'}`}>Time off found in this period</p>
                            <p className="text-xs text-slate-500 mb-3">
                                {/* abs0-7c4e9d21-i — the old second sentence described the bug in block -a, next to a "Paid" button that never paid anything. After -a BOTH decisions are inert, so this is now a recorded note, matching the already-honest stat-holiday copy at :1688-1690. Deliberately "saves", not "exports": the decision persists to payrollDrafts but does NOT reach the CSV. Seam 4 makes it real and this reverts. */}
                                Record whether each absence is paid or unpaid. Spatchy saves your decision but does <strong>not</strong> change gross pay — apply leave pay in your payroll software.
                            </p>
                            <div className="space-y-2">
                                {payRows.flatMap(row =>
                                    row.timeOffEntries.map(entry => {
                                        const startField = entry.startDate || entry.start;
                                        const endField = entry.endDate || entry.end;
                                        const key = `${row.userId}_${startField}`;
                                        const dec = timeOffDecisions[key];
                                        const nights = (() => {
                                            try {
                                                if (!startField || !endField) return 1;
                                                const s = new Date(startField + 'T12:00:00');
                                                const e2 = new Date(endField + 'T12:00:00');
                                                return Math.round((e2 - s) / 86400000) + 1;
                                            } catch { return 1; }
                                        })();
                                        return (
                                            <div key={key} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2.5 shadow-sm">
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm font-semibold text-slate-700">{row.name}</span>
                                                    <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${
                                                        entry.type === 'Vacation' ? 'bg-sky-100 text-sky-700' :
                                                        entry.type === 'Sick' ? 'bg-rose-100 text-rose-700' :
                                                        'bg-slate-100 text-slate-600'
                                                    }`}>{entry.type}</span>
                                                    <span className="text-xs text-slate-400 ml-2">
                                                        {startField === endField
                                                            ? format(new Date(startField + 'T12:00:00'), 'MMM d')
                                                            : `${format(new Date(startField + 'T12:00:00'), 'MMM d')} – ${format(new Date(endField + 'T12:00:00'), 'MMM d')}`
                                                        }
                                                        {' '}· {nights} day{nights !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <button
                                                        onClick={() => setTimeOffDecisionAndPersist(key, 'paid')}
                                                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dec === 'paid' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'}`}
                                                    >
                                                        ✓ Paid
                                                    </button>
                                                    <button
                                                        onClick={() => setTimeOffDecisionAndPersist(key, 'unpaid')}
                                                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dec === 'unpaid' ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-rose-50 hover:text-rose-700'}`}
                                                    >
                                                        Unpaid
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Save absences button */}
                            {/* abs0b-3f7a2c91-c — neutral divider; a sky rule inside a settled slate panel looks like a rendering fault. Chrome follows the panel tint; the Save button beneath it deliberately does not. */}
                            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-200">
                                <button
                                    onClick={saveTimeOffDecisions}
                                    disabled={timeOffSaving}
                                    className="px-4 py-1.5 bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 text-white text-xs font-semibold rounded-lg transition-colors"
                                >
                                    {timeOffSaving ? 'Saving…' : 'Save Absences'}
                                </button>
                                {timeOffSaved && (
                                    <span className="text-xs font-medium text-emerald-600">✓ Saved</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SUMMARY CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Total Gross Payroll</p>
                    <p className="text-2xl font-bold text-slate-800">${totalGross.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    {/* paidhrs-4n1 — gross now computed from paid hours (== shift hours until a break is unpaid) */}
                    <p className="text-xs text-slate-400 mt-1">Paid hours basis · before deductions</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Total Shift Hours</p>
                    <p className="text-2xl font-bold text-slate-800">{totalHours.toFixed(2)}<span className="text-sm font-normal text-slate-400 ml-1">hrs</span></p>
                    {/* paidhrs-4m1 — paid line only on a gap; all-paid periods read exactly as before */}
                    {(totalHours - totalPaidHours) > 0.001
                        ? <p className="text-xs text-amber-600 font-medium mt-1">{totalPaidHours.toFixed(2)} hrs paid · {(totalHours - totalPaidHours).toFixed(2)} unpaid</p>
                        : <p className="text-xs text-slate-400 mt-1">Clocked in/out across all employees</p>}
                </div>
                <div className={`bg-white rounded-xl border p-5 shadow-sm ${allApproved ? 'border-emerald-200' : 'border-amber-200'}`}>
                    <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Approval Status</p>
                    <p className={`text-2xl font-bold ${allApproved ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {approvedCount} / {payRows.length}
                    </p>
                    <p className={`text-xs mt-1 font-medium ${allApproved ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {allApproved ? '✓ All timesheets approved' : `${payRows.length - approvedCount} pending approval`}
                    </p>
                </div>
            </div>

            {/* PER-EMPLOYEE TABLE */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                        <Users size={16} className="text-slate-400" /> Employee Pay Summary
                    </h3>
                </div>

                <div className="divide-y divide-slate-100">
                    {payRows.map((row, index) => {
                        const isExpanded = expandedEmployeeId === row.userId;
                        const color = getAvatarColor(index);
                        const hasMissingDay = row.missingShiftDays.length > 0;
                        const hasTimeOff = row.timeOffEntries.length > 0;
                        return (
                            <React.Fragment key={row.userId}>
                                {/* Summary row */}
                                <div
                                    className={`flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                                    onClick={() => setExpandedEmployeeId(isExpanded ? null : row.userId)}
                                >
                                    <div className="relative">
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ring-2 ${color.bg} ${color.text} ${color.ring}`}>
                                            {getInitials(row.name)}
                                        </div>
                                        {/* Badge: missing shift or time off */}
                                        {(hasMissingDay || hasTimeOff) && (
                                            <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${hasMissingDay ? 'bg-rose-500' : 'bg-sky-400'}`} />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-slate-800 text-sm">{row.name}</p>
                                        {employmentLabel(row.userStatus) && (
                                            <span className="inline-block mt-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{employmentLabel(row.userStatus)}</span>
                                        )}{/* 6f0d3a89 */}
                                        <p className="text-xs text-slate-400 mt-0.5">{row.wage.display}</p>
                                    </div>
                                    <div className="hidden sm:flex items-center gap-6 flex-shrink-0">
                                        <div className="text-right">
                                            <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Pay Type</p>
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${row.wage.type === 'Salary' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`}>
                                                {row.wage.type}
                                            </span>
                                        </div>
                                        <div className="w-px h-8 bg-slate-100" />
                                        {/* PHASE 1: show shift hours as payroll basis, job hours as sub-label */}
                                        <div className="text-right w-24">
                                            <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Shift / Job hrs</p>
                                            {row.wage.type === 'Salary'
                                                ? <span className="text-slate-300 font-normal text-xs">flat rate</span>
                                                : <>
                                                  <p className="text-sm font-bold text-slate-700 tabular-nums mt-0.5">
                                                    {row.totalShiftHours.toFixed(2)}h
                                                    <span className="text-xs font-normal text-slate-400 ml-1">/ {row.totalJobHours.toFixed(2)}h</span>
                                                  </p>
                                                  {/* paidhrs-4o1 — paid basis shown only when it differs from clock */}
                                                  {(row.totalShiftHours - row.totalPaidHours) > 0.001 && (
                                                    <p className="text-xs font-medium text-amber-600 tabular-nums">{row.totalPaidHours.toFixed(2)}h paid</p>
                                                  )}
                                                  </>
                                            }
                                        </div>
                                        <div className="w-px h-8 bg-slate-100" />
                                        <div className="text-right w-28">
                                            <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Gross Pay</p>
                                            {/* abs0-7c4e9d21-f — the "−Nh unpaid" annotation is gone with the deduction; it can never fire again */}
                                            <p className="text-sm font-bold text-slate-800 tabular-nums mt-0.5">
                                                ${formatMoney(row.grossPay)}
                                            </p>
                                        </div>
                                        <div className="w-px h-8 bg-slate-100" />
                                        <div className="w-20 text-right">
                                            <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Status</p>
                                            {row.isApproved
                                                ? <span className="text-xs font-semibold text-emerald-600 mt-0.5 inline-block">✓ Approved</span>
                                                : <span className="text-xs font-semibold text-amber-500 mt-0.5 inline-block">⚠ Pending</span>
                                            }
                                        </div>
                                    </div>
                                    {isExpanded
                                        ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" />
                                        : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
                                    }
                                </div>

                                {/* Expanded breakdown */}
                                {isExpanded && (
                                    <div className="bg-slate-50 border-t border-slate-100 px-5 py-4 space-y-4">

                                        {/* Missing shift callout — inline Add Shift */}
                                        {hasMissingDay && (
                                            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-3">
                                                <p className="text-xs font-semibold text-rose-700 mb-2">No shift recorded on these days — add one below:</p>
                                                <div className="space-y-2">
                                                    {row.missingShiftDays.map(dayKey => {
                                                        const draftKey = `${row.userId}_${dayKey}`;
                                                        const draft = addShiftDraft[draftKey] || {};
                                                        const isSaved = draft.saved;
                                                        return (
                                                            <div key={draftKey} className={`rounded-md border px-3 py-2.5 ${isSaved ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-rose-100'}`}>
                                                                <p className="text-xs font-medium text-slate-600 mb-2">{format(new Date(dayKey + 'T12:00:00'), 'EEEE, MMM d')}</p>
                                                                {!isSaved ? (
                                                                    <div className="flex flex-wrap items-end gap-2">
                                                                        <div>
                                                                            <label className="block text-xs text-slate-400 mb-1">In</label>
                                                                            <input
                                                                                type="time"
                                                                                value={draft.start || '08:00'}
                                                                                onChange={e => setAddShiftDraft(p => ({ ...p, [draftKey]: { ...p[draftKey], start: e.target.value } }))}
                                                                                className="border border-slate-200 rounded px-2 py-1 text-xs text-slate-700 bg-white focus:ring-1 focus:ring-rose-300 outline-none"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-xs text-slate-400 mb-1">Out</label>
                                                                            <input
                                                                                type="time"
                                                                                value={draft.end || '17:00'}
                                                                                onChange={e => setAddShiftDraft(p => ({ ...p, [draftKey]: { ...p[draftKey], end: e.target.value } }))}
                                                                                className="border border-slate-200 rounded px-2 py-1 text-xs text-slate-700 bg-white focus:ring-1 focus:ring-rose-300 outline-none"
                                                                            />
                                                                        </div>
                                                                        {draft.start && draft.end && (
                                                                            <span className="text-xs text-slate-400 pb-1">
                                                                                {(() => {
                                                                                    const [sh, sm] = (draft.start || '08:00').split(':').map(Number);
                                                                                    const [eh, em] = (draft.end || '17:00').split(':').map(Number);
                                                                                    const hrs = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                                                                                    return hrs > 0 ? `${hrs.toFixed(2)}h` : '—';
                                                                                })()}
                                                                            </span>
                                                                        )}
                                                                        <button
                                                                            onClick={() => handleAddShift(row.userId, row.name, dayKey)}
                                                                            disabled={draft.saving}
                                                                            className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded transition-colors disabled:opacity-50"
                                                                        >
                                                                            {draft.saving ? 'Saving…' : '+ Add'}
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-xs text-emerald-600 font-medium">✓ {draft.start} – {draft.end} saved. Refresh to see updated hours.</p>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Time off in period (per-employee) */}
                                        {hasTimeOff && (
                                            <div className="border-l-4 border-sky-300 pl-4">
                                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Time Off This Period</h4>
                                                <div className="space-y-1">
                                                    {row.timeOffEntries.map((entry, ei) => {
                                                        const startField = entry.startDate || entry.start; const endField = entry.endDate || entry.end; const key = `${row.userId}_${startField}`;
                                                        const dec = timeOffDecisions[key];
                                                        return (
                                                            <div key={ei} className="flex items-center gap-2 text-xs">
                                                                <span className={`px-2 py-0.5 rounded-full font-medium ${
                                                                    entry.type === 'Vacation' ? 'bg-sky-100 text-sky-700' :
                                                                    entry.type === 'Sick' ? 'bg-rose-100 text-rose-700' :
                                                                    'bg-slate-100 text-slate-600'
                                                                }`}>{entry.type}</span>
                                                                <span className="text-slate-500">
                                                                    {startField === endField ? format(new Date(startField + 'T12:00:00'), 'MMM d') : `${format(new Date(startField + 'T12:00:00'), 'MMM d')}–${format(new Date(endField + 'T12:00:00'), 'MMM d')}`}
                                                                </span>
                                                                <span className={`ml-auto font-semibold ${dec === 'paid' ? 'text-emerald-600' : dec === 'unpaid' ? 'text-rose-500' : 'text-amber-500'}`}>
                                                                    {/* abs0-7c4e9d21-j — the qualifier goes away in Seam 4, when absence hours reach gross */}
                                                                    {dec === 'paid' ? 'Paid · not in gross' : dec === 'unpaid' ? 'Unpaid · not in gross' : '⚠ Confirm above'}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Job breakdown */}
                                        <div className="border-l-4 border-indigo-400 pl-4">
                                            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Job Breakdown — This Period</h4>
                                            {row.allJobs.length > 0 ? (
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="text-xs text-slate-400 border-b border-slate-200">
                                                            <th className="text-left pb-1 font-medium pr-6">Job</th>
                                                            <th className="text-left pb-1 font-medium pr-6">Date</th>
                                                            <th className="text-right pb-1 font-medium pr-6">Allocated</th>
                                                            <th className="text-right pb-1 font-medium">Clocked</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {row.allJobs.map((job, i) => {
                                                            const clocked = parseFloat(job.clockedHours) || null;
                                                            const allocated = job.allocatedHours !== null ? parseFloat(job.allocatedHours) : null;
                                                            const isOver = clocked !== null && allocated !== null && clocked > allocated;
                                                            return (
                                                                <tr key={i} className="border-b border-slate-100 last:border-0">
                                                                    <td className="py-1.5 pr-6 text-slate-700 font-medium">{job.jobTitle || '—'}</td>
                                                                    <td className="py-1.5 pr-6 text-slate-400 text-xs">{format(new Date(job.dayKey + 'T12:00:00'), 'MMM d')}</td>
                                                                    <td className="py-1.5 pr-6 text-right text-slate-400 font-mono text-xs">
                                                                        {allocated !== null ? `${allocated.toFixed(2)}h` : '—'}
                                                                    </td>
                                                                    <td className={`py-1.5 text-right font-mono text-xs font-semibold ${isOver ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                                        {clocked !== null ? `${clocked.toFixed(2)}h` : `${parseFloat(job.hours || 0).toFixed(2)}h`}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p className="text-sm text-slate-400 italic">No job entries for this period.</p>
                                            )}
                                            {row.wage.type === 'Salary' && (
                                                <p className="text-xs text-slate-400 mt-3 italic">
                                                    Salaried employee — gross pay is ${formatMoney(row.grossPay)} regardless of hours logged
                                                    ({periodsPerYear === 26 ? 'bi-weekly' : 'weekly'} pay period, ${formatMoney(row.wage.annualSalary)}/yr ÷ {periodsPerYear} periods).
                                                </p>
                                            )}
                                        </div>

                                        {/* Hours reconciliation for hourly employees */}
                                        {row.wage.type === 'Hourly' && (
                                            <div className="border-l-4 border-slate-300 pl-4">
                                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Hours Reconciliation</h4>
                                                <div className="grid grid-cols-3 gap-3 text-center">
                                                    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                                                        {/* paidhrs-4p1 — this grid reconciles CLOCK time vs job punches; after 3c 'payroll' means paid hours, so the clock cell is relabelled to avoid implying clock == pay */}
                                                        <p className="text-xs text-slate-400">Shift (clock)</p>
                                                        <p className="text-base font-bold text-slate-700 mt-0.5">{row.totalShiftHours.toFixed(2)}h</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                                                        <p className="text-xs text-slate-400">On jobs</p>
                                                        <p className="text-base font-bold text-slate-700 mt-0.5">{row.totalJobHours.toFixed(2)}h</p>
                                                    </div>
                                                    <div className={`rounded-lg border px-3 py-2 ${row.totalShiftHours - row.totalJobHours < 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
                                                        <p className="text-xs text-slate-400">Non-job time</p>
                                                        <p className={`text-base font-bold mt-0.5 ${row.totalShiftHours - row.totalJobHours < 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                                                            {Math.max(0, row.totalShiftHours - row.totalJobHours).toFixed(2)}h
                                                        </p>
                                                    </div>
                                                </div>
                                                {/* actbreak-3e-render — break the SHIFT CLOCK out by activity so travel (etc.) can be priced for
                                                    the period. Shown whenever the period has shift hours; Work is ALWAYS listed (unlike the
                                                    day-header chips where it is the implicit baseline) so the parts visibly sum to the Shift (clock)
                                                    cell above — a pure-work/legacy period shows a single Work chip = the clock. Custom labels are the
                                                    SNAPSHOTTED activityName; legacy no-activityType shifts fold into Work. Partitions the clock (one
                                                    source, sums exactly) — NOT the Non-job residual. */}
                                                {row.activityBreakdown.length > 0 && (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Where the clock went</p>
                                                        <div className="flex flex-wrap items-center gap-1.5">
                                                            {row.activityBreakdown.map(a => {
                                                                // period-scope paid/unpaid is 3-state, NOT the day chip's binary: a period can span a
                                                                // paid→unpaid settings change, so a bucket can be MIXED. Never assert "paid" for partly-unpaid time.
                                                                const unpaidHrs = Math.max(0, a.hours - a.paidHours);
                                                                const fullyUnpaid = a.paidHours < 0.005;
                                                                const mixed = !fullyUnpaid && unpaidHrs >= 0.005;
                                                                const cls = fullyUnpaid ? 'bg-slate-100 text-slate-500 border-slate-200'
                                                                    : mixed ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                                    : 'bg-violet-50 text-violet-700 border-violet-200';
                                                                const suffix = fullyUnpaid ? ' · unpaid' : mixed ? ` · ${unpaidHrs.toFixed(2)}h unpaid` : '';
                                                                const title = `${a.label} — ${a.hours.toFixed(2)}h`
                                                                    + (fullyUnpaid ? ', unpaid' : mixed ? ` (${a.paidHours.toFixed(2)}h paid, ${unpaidHrs.toFixed(2)}h unpaid)` : '');
                                                                return (
                                                                    <span key={a.key} className={`text-xs px-2 py-0.5 rounded border ${cls}`} title={title}>
                                                                        {a.label} <span className="font-semibold">{a.hours.toFixed(2)}h</span>{suffix}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                        <p className="text-xs text-slate-400 mt-1.5 italic">These add up to the Shift (clock) total above.</p>
                                                    </div>
                                                )}
                                                <p className="text-xs text-slate-400 mt-2 italic">Non-job time is clocked time not captured by a job timer (Shift clock − On jobs) — a utilisation gap, not a sum of activities. Travel, break, shop and custom hours are in the clock breakdown above.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Totals footer */}
                <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-600">Total Gross Payroll</p>
                    <p className="text-lg font-bold text-slate-800">${totalGross.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
            </div>

            {/* ACTIONS */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
                <div>
                    <p className="text-xs text-slate-400 max-w-sm">
                        Export a CSV to import into your payroll software. Mark the period as paid once wages have been processed.
                    </p>
                    {(hasMissingShifts || hasPendingTimeOff || hasPendingStats) && (
                        <p className="text-xs text-amber-500 font-medium mt-1">
                            ⚠ {[hasMissingShifts && 'missing shifts', hasPendingTimeOff && 'unconfirmed time off', hasPendingStats && 'unreviewed stat holidays'].filter(Boolean).join(', ')} — review before exporting.
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 border border-slate-200 bg-white text-sm font-medium text-slate-700 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                    >
                        <Download size={15} />
                        Export CSV
                    </button>
                    {paidRun ? (
                        <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-lg border border-emerald-200">
                            <CheckCircle2 size={15} />
                            Paid on {format(paidRun.paidAt?.toDate ? paidRun.paidAt.toDate() : new Date(paidRun.paidAt), 'MMM d, yyyy')}
                        </div>
                    ) : (
                        <button
                            onClick={onMarkAsPaid}
                            disabled={!allApproved}
                            title={!allApproved ? 'All timesheets must be approved before marking as paid' : ''}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors shadow-sm
                                ${allApproved
                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                }`}
                        >
                            <CheckCircle2 size={15} />
                            Mark Period as Paid
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- MAIN VIEW ---
const TimesheetsView = ({ currentUserProfile, onApproveTimesheet, onJobSelect }) => {
    const [jobs, setJobs] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [approvedTimesheets, setApprovedTimesheets] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const [payrollSettings, setPayrollSettings] = useState(null);
    const [currentPeriodStart, setCurrentPeriodStart] = useState(null);
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');
    const [timesheetData, setTimesheetData] = useState({});

    // tsmgr-5a2c8e71 — was an inline 5-string array missing 'Owner / System Admin'; now THE approver
    // list (permissions.js JOB_TIME_APPROVER_ROLES == firestore.rules isHighLevel()), the same one
    // that lets the role open this screen at all.
    const canApprove = canCorrectJobTime(currentUserProfile);
    // tsmgr-5a2c8e71 — money (rates, gross, Payroll Summary, CSV, Mark as paid) is Owner/Admin
    // only. A Manager approves and corrects hours but this screen neither shows nor FETCHES pay
    // for them. Display choice, not a security boundary — see canViewPayroll in permissions.js.
    const canViewPay = canViewPayroll(currentUserProfile);

    const [activeTab, setActiveTab] = useState('timesheets'); // 'timesheets' | 'payroll'
    const [paidRun, setPaidRun] = useState(null);
    const [orgProvince, setOrgProvince] = useState(''); // for stat holiday lookup
    const [orgPayrollDefaults, setOrgPayrollDefaults] = useState(DEFAULT_ORG_PAYROLL_DEFAULTS);

    // --- ALL DATA FETCHING UNCHANGED ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const fetchSettings = async () => {
            const orgDocRef = doc(db, 'organizations', currentUserProfile.organizationId);
            const docSnap = await getDoc(orgDocRef);
            if (docSnap.exists()) {
                const orgData = docSnap.data();
                // Capture province for stat holiday awareness
                setOrgProvince(orgData.state || '');
                if (orgData.payrollDefaults) {
                    setOrgPayrollDefaults({ ...DEFAULT_ORG_PAYROLL_DEFAULTS, ...orgData.payrollDefaults });
                }
                if (orgData.payrollSettings) {
                    const settings = orgData.payrollSettings;
                    settings.startDate = settings.startDate.toDate();
                    setPayrollSettings(settings);
                    setCurrentPeriodStart(getCurrentPeriodStart(settings));
                } else {
                    const defaultSettings = { frequency: 'weekly', startDate: startOfWeek(new Date(), { weekStartsOn: 1 }) };
                    setPayrollSettings(defaultSettings);
                    setCurrentPeriodStart(getCurrentPeriodStart(defaultSettings));
                }
            } else {
                const defaultSettings = { frequency: 'weekly', startDate: startOfWeek(new Date(), { weekStartsOn: 1 }) };
                setPayrollSettings(defaultSettings);
                setCurrentPeriodStart(getCurrentPeriodStart(defaultSettings));
            }
        };
        fetchSettings();
    }, [currentUserProfile?.organizationId]);

    useEffect(() => {
        if (currentPeriodStart && payrollSettings) {
            setDateRange(getPeriodRange(currentPeriodStart, payrollSettings.frequency));
        }
    }, [currentPeriodStart, payrollSettings]);

    useEffect(() => {
        const fetchEmployees = async (orgId) => {
            if (!canApprove) { setEmployees([]); return; }
            try {
                const usersRef = collection(db, 'users');
                // 9b3e5a12 — load ALL org users, not just status==active. Active users always
                // get a row; non-active users (inactive/invited/removed/…) are shown ONLY when
                // they have recorded hours in the period — that inclusion is decided downstream
                // (grid: getOrCreateDay promote; payroll: the payRows filter), after shifts load.
                // Single-field query → no composite index needed.
                const q = query(usersRef, where("organizationId", "==", orgId));
                const querySnapshot = await getDocs(q);
                // compcut-8e2f4a17 — pay comes from private/comp ONLY. Any pay still on the
                // main user record (legacy dual-write copy, or a value a client wrote there) is
                // dropped here, so it can never fill a field the private copy lacks.
                const baseEmployees = querySnapshot.docs.map(doc => withoutMainRecordPay({ id: doc.id, ...doc.data() }));

                // r8-comp-private-5d2c9f41 — wages (hourlyRate/salaryWage) live in
                // users/{id}/private/comp, readable only by Owner/Admin/Manager. This whole
                // fetch is already behind canApprove (the same role set), so the reads succeed;
                // getEmployeeWage keeps reading emp.hourlyRate / emp.salaryWage off the merged
                // objects and needs no change. A failed read degrades to "no rate set" rather
                // than breaking the timesheet grid.
                const withComp = await Promise.all(baseEmployees.map(async (emp) => {
                    // profcut-4b9d2e63 — timeOffHistory (payroll inclusion, unpaid deduction, CSV
                    // "Time Off" column) and the days-taken counters live in users/{id}/private/profile,
                    // same Owner/Admin/Manager gate as comp. A key there wins over a not-yet-moved
                    // main-record copy; a failed read keeps the main-record value, like comp.
                    let merged = emp;
                    try {
                        const profileSnap = await getDoc(doc(db, 'users', emp.id, 'private', 'profile'));
                        if (profileSnap.exists()) merged = resolveProfileRead(emp, profileSnap.data());
                    } catch (err) {
                        console.warn(`Could not load time off for ${emp.id}:`, err?.message);
                    }
                    // tsmgr-5a2c8e71 — no pay read at all for a Manager (nothing on their view uses
                    // it). Hours don't need it: compensationType and the work schedule that drive
                    // the salaried-hours estimate live on the main record, not in private/comp.
                    if (!canViewPay) return merged;
                    try {
                        const compSnap = await getDoc(doc(db, 'users', emp.id, 'private', 'comp'));
                        return compSnap.exists() ? { ...merged, ...compSnap.data() } : merged;
                    } catch (err) {
                        console.warn(`Could not load compensation for ${emp.id}:`, err?.message);
                        return merged;
                    }
                }));

                setEmployees(withComp);
            } catch (error) {
                console.error("Error fetching employees:", error);
                setEmployees([]);
            }
        };

        if (currentUserProfile?.id) {
            const currentEmployeeData = withoutMainRecordPay({ id: currentUserProfile.id, name: currentUserProfile.name, ...currentUserProfile }); // compcut-8e2f4a17
            setEmployees(prev => {
                if (prev.find(e => e.id === currentUserProfile.id)) return prev;
                return [currentEmployeeData, ...prev];
            });
        }

        if (currentUserProfile?.organizationId) fetchEmployees(currentUserProfile.organizationId);
        else setEmployees([]);
    }, [currentUserProfile?.organizationId, currentUserProfile?.id, currentUserProfile?.name, canApprove, canViewPay]);

    useEffect(() => {
        if (!currentUserProfile?.organizationId || !dateRange.start || !dateRange.end) return;
        setIsLoading(true);
        const orgId = currentUserProfile.organizationId;
        const { start, end } = dateRange;
        const queryEndDate = startOfDay(addDays(end, 1));

        const filterByUserId = selectedEmployeeId !== 'all';
        let targetUserId = null;
        if (filterByUserId) targetUserId = selectedEmployeeId;
        else if (!canApprove) targetUserId = currentUserProfile.id;

        const qShiftBase = collectionGroup(db, 'shifts');
        const qJobsBase = collection(db, `organizations/${orgId}/jobs`);

        let qShifts = query(qShiftBase,
            where("organizationId", "==", orgId),
            where("shiftStart", ">=", Timestamp.fromDate(start)),
            where("shiftStart", "<", Timestamp.fromDate(queryEndDate))
        );
        // jobday-6b2f9e14 — reach past the period on BOTH sides (a punch can precede its job's
        // scheduled day as well as follow it; the census saw -16 as well as +27). Every deposit
        // out of this widened set is range-guarded back to the period in the calc effect, so a
        // padded job can never render a day row outside the period being viewed.
        let qJobs = query(qJobsBase,
            where("startAt", ">=", Timestamp.fromDate(subDays(start, JOB_PUNCH_LOOKBACK_DAYS))),
            where("startAt", "<", Timestamp.fromDate(addDays(queryEndDate, JOB_PUNCH_LOOKBACK_DAYS)))
        );
        if (targetUserId) {
            qShifts = query(qShifts, where("userId", "==", targetUserId));
            qJobs = query(qJobs, where("assignedTo", "array-contains", targetUserId));
        }

        const unsubscribeJobs = onSnapshot(qJobs, (snapshot) => {
            setJobs(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); // s6costing-docid: doc id wins over a stale stored `id` field
        });
        const unsubscribeShifts = onSnapshot(qShifts, (snapshot) => {
            setShifts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching shifts:", error);
            setIsLoading(false);
        });

        const approvedTimesheetsRef = collection(db, `organizations/${orgId}/timesheets`);
        let qApprovedTimesheets = query(approvedTimesheetsRef,
            where("weekStart", ">=", Timestamp.fromDate(start)),
            where("weekStart", "<=", Timestamp.fromDate(end))
        );
        if (targetUserId) qApprovedTimesheets = query(qApprovedTimesheets, where("userId", "==", targetUserId));

        const unsubscribeApproved = onSnapshot(qApprovedTimesheets, (snapshot) => {
            const approvals = {};
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.status === 'Approved') approvals[data.userId] = true;
            });
            setApprovedTimesheets(approvals);
        });

        return () => { unsubscribeJobs(); unsubscribeShifts(); unsubscribeApproved(); };
    }, [currentUserProfile, dateRange, selectedEmployeeId, canApprove]);

    // --- CALCULATION LOGIC (unchanged + sold vs actual fields) ---
    useEffect(() => {
        if (employees.length === 0) return;

        const employeeData = {};
        let employeesToProcess = [];
        if (selectedEmployeeId === 'all') {
            // 7c2f8e40 — only ACTIVE users get a guaranteed (possibly empty) row. Non-active
            // users are promoted into the grid by getOrCreateDay only if they have hours.
            employeesToProcess = employees.filter(isActiveAccount);
        } else {
            const target = employees.find(e => e.id === selectedEmployeeId);
            if (target) employeesToProcess = [target];
            else if (selectedEmployeeId === currentUserProfile?.id) {
                employeesToProcess = employees.filter(e => e.id === currentUserProfile?.id);
                if (employeesToProcess.length === 0) employeesToProcess = [{ id: currentUserProfile.id, name: currentUserProfile.name }];
            }
        }

        employeesToProcess.forEach(emp => {
            // f1e7c294 — userStatus carries the ACCOUNT status (active/invited/inactive/…) so the
            // card can render the employment label. NOTE: the sibling `status` field is the
            // APPROVAL status ('Pending'/'Approved') and is unrelated — do not conflate them.
            employeeData[emp.id] = { name: emp.name || 'Unknown', status: 'Pending', userStatus: emp.status || null, totalShiftHours: 0, totalJobHours: 0, dailyData: {} };
        });

        // f1e7c294 — allowPromote lets a shift/labor entry surface a NON-ACTIVE org user who
        // has hours in the period. A user unknown to the org (no matching users doc) is still
        // dropped, exactly as before. Display-only job punches pass allowPromote=false.
        const getOrCreateDay = (userId, date, allowPromote = false) => {
            if (!employeeData[userId]) {
                if (!allowPromote) return null;
                const u = employees.find(e => e.id === userId);
                if (!u) return null;
                employeeData[userId] = { name: u.name || 'Unknown', status: 'Pending', userStatus: u.status || null, totalShiftHours: 0, totalJobHours: 0, dailyData: {} };
            }
            const dayKey = format(date, 'yyyy-MM-dd');
            if (!employeeData[userId].dailyData[dayKey]) {
                employeeData[userId].dailyData[dayKey] = {
                    dayDisplay: format(date, 'EEEE, MMM d'),
                    // jobpunch-9a3f21c7 — Phase 1 display-only job-timer punches for this user+day.
                    // In-memory only (never persisted, never summed into any payroll figure).
                    // paidhrs-4b1 — dailyPaidHours mirrors dailyShiftHours but counts only paid shifts (isShiftPaid). Absent flag = paid, so all-paid days stay identical.
                    shiftStart: null, shiftEnd: null, rawShifts: [], jobs: [], jobPunches: [], dailyShiftHours: 0, dailyPaidHours: 0,
                };
            }
            return employeeData[userId].dailyData[dayKey];
        };

        shifts.forEach(shift => {
            const shiftStart = safeToDate(shift.shiftStart);
            const shiftEnd = safeToDate(shift.shiftEnd);
            if (!shiftStart) return;
            const day = getOrCreateDay(shift.userId, shiftStart, true); // 3e8b6d15 — a shift can surface a non-active user with hours
            if (!day) return;

            if (shiftStart && shiftEnd) {
                const duration = shiftDurationHours(shift); // 7e41a0b3 — identical arithmetic, now via the shared shiftTime.js twin
                if (!day.rawShifts) day.rawShifts = [];
                // 7f3a9c21 — carry the shift's own audit fields so the editor can prefill and keep originals non-lossy
                // 7c1e9a04 — also carry provenance booleans so DayRow can render the self-reported / edited badges
                // shiftlabel-w-4a7c9e21 — carry the activity label + travel destination so DayRow can chip the row
                // paidhrs-4c1 — carry the frozen paid flag onto the projection so the per-shift row can mark unpaid shifts.
                // collapse-3e-proj — carry the SNAPSHOTTED custom-activity name (frozen at clock-in by
                // mobile, ShiftClockContext 581-582); the header chip reads this, never the live org map.
                day.rawShifts.push({ id: shift.id, shiftStart, shiftEnd, duration, paid: isShiftPaid(shift), originalStart: shift.originalStart || null, originalEnd: shift.originalEnd || null, editHistory: shift.editHistory || [], shiftEndSelfReported: shift.shiftEndSelfReported || false, editedByAdmin: shift.editedByAdmin || false, activityType: shift.activityType || null, activityName: shift.activityName || null, travelToLabel: shift.travelToLabel || null });
                day.dailyShiftHours += duration;
                day.dailyPaidHours += isShiftPaid(shift) ? duration : 0; // paidhrs-4c1
                if (!day.shiftStart || shiftStart < day.shiftStart) day.shiftStart = shiftStart;
                if (!day.shiftEnd || shiftEnd > day.shiftEnd) day.shiftEnd = shiftEnd;
            } else if (shiftStart && !shiftEnd) {
                // Dangling shift — push to rawShifts so the editor can find its ID for updateDoc
                // 4e8b1d60 — same audit-field passthrough as complete shifts (originals stay non-lossy across edits)
                // 7c1e9a04 — provenance booleans for the badge (dangling shift can be self-reported once closed, or admin-edited)
                // shiftlabel-w-4a7c9e21 — same label passthrough for an open record (an open break/travel can dangle too)
                // paidhrs-4d1 — open shift contributes no hours; carry paid only so an open unpaid record still marks correctly.
                day.rawShifts.push({ id: shift.id, shiftStart, shiftEnd: null, paid: isShiftPaid(shift), originalStart: shift.originalStart || null, originalEnd: shift.originalEnd || null, editHistory: shift.editHistory || [], shiftEndSelfReported: shift.shiftEndSelfReported || false, editedByAdmin: shift.editedByAdmin || false, activityType: shift.activityType || null, activityName: shift.activityName || null, travelToLabel: shift.travelToLabel || null });
                if (!day.shiftStart || shiftStart < day.shiftStart) day.shiftStart = shiftStart;
                day.shiftEnd = null;
            }
        });

        // jobday-6b2f9e14 — the job query now reaches JOB_PUNCH_LOOKBACK_DAYS either side of the
        // period, so `jobs` contains jobs scheduled outside it. Every deposit below is guarded by
        // this, or a padded job would render day rows belonging to another period entirely.
        // Same bounds the query uses for shifts: inclusive start, exclusive next midnight.
        const periodEndExclusive = startOfDay(addDays(dateRange.end, 1));
        const inPeriod = (d) => !!d && d >= dateRange.start && d < periodEndExclusive;

        jobs.forEach(job => {
            const jobDate = safeToDate(job.startAt);
            if (!jobDate) return;

            // One entry per user per job. Clocked entries carry source:'clocked'
            // and preserve allocatedHours from the original estimate.
            const seenUsers = new Set();
            (job.jobCosting?.laborEntries || []).forEach(entry => {
                if (!entry.userId || seenUsers.has(entry.userId)) return;
                seenUsers.add(entry.userId);

                // jobday-6b2f9e14 — deposit one job row onto a day, reusing the existing per-job
                // dedupe. That dedupe stays per-DAY and stays correct now that one job can appear
                // on several days: the server upsert guarantees at most ONE laborEntries row per
                // person per job (verified: 0 of 299 rows in production break that), so a single
                // day can never receive the same job twice for the same person.
                const deposit = (anchor, fields) => {
                    if (!inPeriod(anchor)) return;
                    const day = getOrCreateDay(entry.userId, anchor, true); // 5a9c2f7b — job-labor hours can surface a non-active user too
                    if (!day) return;
                    const existingIndex = day.jobs.findIndex(j => j.jobData?.id === job.id);
                    const jobEntry = {
                        jobData: job,
                        jobTitle: job.title,
                        address: job.address,
                        ...fields,
                    };
                    if (existingIndex >= 0) day.jobs[existingIndex] = jobEntry;
                    else day.jobs.push(jobEntry);
                };

                // A MANUAL row (no source:'clocked') has no punches at all — 193 of 299 labor rows
                // in production are these. There is no signal about when that work happened, so it
                // stays on the scheduled day. Inventing a day for it would be worse than the bug
                // this seam fixes.
                if (entry.source !== 'clocked') {
                    deposit(jobDate, {
                        hours: entry.hours,
                        allocatedHours: entry.hours,
                        clockedHours: null,
                        jobTotalClockedHours: null,
                        isContinuation: false,
                        isResidual: false,
                    });
                    return;
                }

                // A CLOCKED row is a cumulative per-person total for the WHOLE job. Split it by the
                // day each punch started — the same total's own inputs, not a different number.
                const billedTotal = parseFloat(entry.hours) || 0;
                const byDay = closedPunchHoursByDay(job.timePunches, entry.userId, d => format(d, 'yyyy-MM-dd'));
                const derivedTotal = sumDayBuckets(byDay);

                // The estimate is ONE number for the whole job, so it is shown ONCE — on the
                // earliest day actually worked. Later days carry allocatedHours null, which renders
                // as a dash explained on hover. Repeating it would make the period's Sold column
                // add up to a multiple of the real estimate.
                const orderedKeys = Array.from(byDay.keys()).sort();
                let estimateShown = false;

                orderedKeys.forEach(key => {
                    const { hours, anchor } = byDay.get(key);
                    const isFirst = !estimateShown && inPeriod(anchor);
                    if (isFirst) estimateShown = true;
                    deposit(anchor, {
                        // Rounded for display at the LAST possible moment — the server rounds its
                        // cumulative total once, at 2dp, over the summed float, so rounding each
                        // day at 2dp and adding them up would drift from what the invoice bills.
                        hours: parseFloat(hours.toFixed(2)),
                        allocatedHours: isFirst ? (entry.allocatedHours ?? null) : null,
                        clockedHours: parseFloat(hours.toFixed(2)),
                        // The over/under comparison is WHOLE ESTIMATE vs WHOLE JOB, so it means the
                        // same thing however many days the job spans. Carried only on the row that
                        // shows the estimate; a continuation row has no estimate to be over.
                        jobTotalClockedHours: isFirst ? billedTotal : null,
                        isContinuation: !isFirst,
                        isResidual: false,
                    });
                });

                // THE RESIDUAL GUARD. Every clocked row in production (106 of 106) already equals
                // the sum of its own punches, so this should never fire. "Should never" is not
                // "cannot": if a clocked row ever holds hours its punches no longer account for,
                // silently dropping them would understate hours the customer was BILLED for. The
                // difference is deposited on the scheduled day and marked, so it shows up on
                // someone's screen instead of disappearing. 0.01 is the server's own rounding
                // granularity (2dp), so ordinary rounding never trips this.
                const residual = parseFloat((billedTotal - derivedTotal).toFixed(2));
                if (Math.abs(residual) >= 0.01 && inPeriod(jobDate)) {
                    const resDay = getOrCreateDay(entry.userId, jobDate, true);
                    if (resDay) {
                        // The scheduled day may ALREADY hold a real worked row for this job. Merge
                        // into it rather than through `deposit`, whose per-job dedupe would replace
                        // it and lose the day's actual hours.
                        const existing = resDay.jobs.find(j => j.jobData?.id === job.id);
                        if (existing) {
                            existing.hours = parseFloat(((parseFloat(existing.hours) || 0) + residual).toFixed(2));
                            existing.clockedHours = parseFloat(((parseFloat(existing.clockedHours) || 0) + residual).toFixed(2));
                            existing.isResidual = true;
                            existing.residualHours = residual;
                        } else {
                            resDay.jobs.push({
                                jobData: job,
                                jobTitle: job.title,
                                address: job.address,
                                hours: residual,
                                allocatedHours: estimateShown ? null : (entry.allocatedHours ?? null),
                                clockedHours: residual,
                                jobTotalClockedHours: null,
                                isContinuation: estimateShown,
                                isResidual: true,
                                residualHours: residual,
                            });
                        }
                    }
                }
            });
        });

        // jobpunch-4c7e88b1 — Phase 1 display-only: match each job-timer punch to the person who
        // made it (punch.userId) on the calendar day it STARTED (punch.punchIn). getOrCreateDay
        // creates the day bucket even with no shift, so a "timer but never clocked in" day still
        // appears. NEVER added to dailyShiftHours or any total — visibility/reconciliation only.
        // jobday-6b2f9e14 — the Phase 1 coverage note that used to sit here (a punch on a job
        // scheduled outside the period could not be shown) is now FIXED by the padded job query:
        // such a job is loaded, and inPeriod below admits the punch on its own day. The guard
        // replaces the old implicit one — the query, not the range, used to do the limiting.
        jobs.forEach(job => {
            (job.timePunches || []).forEach(punch => {
                const punchIn = safeToDate(punch.punchIn);
                if (!punch.userId || !punchIn) return;
                if (!inPeriod(punchIn)) return; // jobday-6b2f9e14 — a padded job's out-of-period punches stay out
                const day = getOrCreateDay(punch.userId, punchIn);
                if (!day) return;
                if (!day.jobPunches) day.jobPunches = [];
                day.jobPunches.push({
                    jobId: job.id,
                    // jobid-3e-proj-4f2b8a — Seam B: carry number + the FULL job doc (a click reuses
                    // onJobSelect(jobData) EXACTLY as the job-hours table does, 206), PLUS the customer +
                    // address that actually identify the job. A recurring service job's title repeats on
                    // every visit ("Lawn Mowing - Weekly"), so customer/address is the only distinguisher.
                    // clientName is a display string from customer.name (JobFormModal 1308) — it defaults to
                    // '' on a customerless job (273/1308), so || null normalizes it; render cascades
                    // customer → address → title so a line never leads with a blank or a dash.
                    jobNumber: job.jobNumber ?? null,
                    jobData: job,
                    clientName: job.clientName || null,
                    address: job.address || null,
                    jobTitle: job.title || 'Untitled job',
                    punchIn,
                    punchOut: safeToDate(punch.punchOut), // null while running
                    // jobday-6b2f9e14 — a voided punch keeps its real times but is set to hours 0,
                    // and a capped one (past the 13h believability limit) likewise. Both would
                    // otherwise render here as ordinary timer lines showing time that counts for
                    // nothing — the same contradiction this seam exists to remove. Carry the flags
                    // so the row can say so. The times themselves STAY: voiding is deliberately not
                    // a delete, because a punch is a record of where someone was.
                    voided: punch.voided === true,
                    capped: punch.needsReview === true,
                });
            });
        });

        Object.entries(employeeData).forEach(([userId, data]) => {
            let weeklyShiftTotal = 0;
            let weeklyPaidTotal = 0; // paidhrs-4e1 — paid hours run alongside clock hours
            let weeklyJobTotal = 0;
            Object.values(data.dailyData).forEach(day => {
                // shiftsort-7b3f1a90 — order each day's punches chronologically so per-entry rows read
                // in sequence and edit/delete target a stable row. The range-filtered query already
                // returns start-ascending, so this is robustness + a deterministic tiebreak when two
                // docs share a shiftStart — NOT the span fix (the span uses the computed min/max).
                if (day.rawShifts) day.rawShifts.sort((a, b) => a.shiftStart - b.shiftStart);
                // overlap-3e-d-7a2f1c — Seam D: flag overlapping punches in one linear pass. rawShifts is
                // now start-ascending (line above), so tracking the running MAX clock-out (prevEnd) and
                // comparing each next start against it catches overlaps INCLUDING containment (a long shift
                // covering later short ones), which an adjacent-only check would miss. Both members of a pair
                // are flagged so each expanded row lands the marker. Touching endpoints (start === prevEnd)
                // are NOT an overlap (strict <), matching saveShift's confirmIfOverlap. Open rows (no
                // shiftEnd) are skipped — the open-shift condition already flags those.
                {
                    let prevEnd = null, prevShift = null;
                    (day.rawShifts || []).forEach(s => {
                        if (!s.shiftStart || !s.shiftEnd) return;
                        if (prevEnd && s.shiftStart < prevEnd) { s._overlaps = true; if (prevShift) prevShift._overlaps = true; }
                        if (!prevEnd || s.shiftEnd > prevEnd) { prevEnd = s.shiftEnd; prevShift = s; }
                    });
                }
                // jobpunch-2f9b6d40 — same chronological ordering for the display-only timer list.
                if (day.jobPunches) day.jobPunches.sort((a, b) => a.punchIn - b.punchIn);
                weeklyShiftTotal += day.dailyShiftHours;
                weeklyPaidTotal += day.dailyPaidHours; // paidhrs-4e1
                day.jobs.forEach(job => { weeklyJobTotal += parseFloat(job.hours) || 0; });
            });
            data.totalShiftHours = weeklyShiftTotal;
            data.totalPaidHours = weeklyPaidTotal; // paidhrs-4e1
            data.totalJobHours = weeklyJobTotal;
            if (approvedTimesheets[userId]) data.status = 'Approved';
        });

        setTimesheetData(employeeData);
        // jobday-6b2f9e14 — dateRange is NEW here: the in-period guard reads it, because the job
        // query now deliberately fetches jobs from outside the period. Without these two deps the
        // buckets would be rebuilt against the previous period's bounds whenever the period moved
        // without `jobs` changing identity.
    }, [jobs, shifts, employees, approvedTimesheets, selectedEmployeeId, currentUserProfile, dateRange.start, dateRange.end]);

    // --- PAYROLL RUN LISTENER — checks if current period is already marked paid ---
    useEffect(() => {
        // tsmgr-5a2c8e71 — only the Payroll Summary tab reads paidRun, so only payroll viewers
        // listen. Before this the listener also ran for Field Technicians, whom the payrollRuns
        // rule (isHighLevel) refuses — a silent permission error on every period change.
        if (!canViewPay) { setPaidRun(null); return; }
        if (!currentUserProfile?.organizationId || !dateRange.start) return;
        const orgId = currentUserProfile.organizationId;
        const periodKey = format(dateRange.start, 'yyyy-MM-dd');
        const runsRef = collection(db, `organizations/${orgId}/payrollRuns`);
        const q = query(runsRef, where('periodStart', '==', periodKey));
        const unsub = onSnapshot(q, (snap) => {
            setPaidRun(snap.empty ? null : snap.docs[0].data());
        });
        return () => unsub();
    }, [currentUserProfile?.organizationId, dateRange.start, canViewPay]);

    // --- MARK PERIOD AS PAID ---
    const handleMarkAsPaid = async () => {
        if (!currentUserProfile?.organizationId || !dateRange.start || !dateRange.end) return;
        const confirmed = window.confirm(
            `Mark the period ${format(dateRange.start, 'MMM d')} – ${format(dateRange.end, 'MMM d, yyyy')} as paid?\n\nThis cannot be undone.`
        );
        if (!confirmed) return;
        try {
            const orgId = currentUserProfile.organizationId;
            await addDoc(collection(db, `organizations/${orgId}/payrollRuns`), {
                periodStart: format(dateRange.start, 'yyyy-MM-dd'),
                periodEnd: format(dateRange.end, 'yyyy-MM-dd'),
                paidAt: serverTimestamp(),
                paidBy: currentUserProfile.name || currentUserProfile.id,
                paidByUserId: currentUserProfile.id,
            });
        } catch (err) {
            console.error('Error marking payroll as paid:', err);
            alert('Something went wrong. Please try again.');
        }
    };

    // --- PERIOD NAVIGATION (unchanged) ---
    const handlePreviousPeriod = () => {
        if (currentPeriodStart && payrollSettings) {
            const periodLength = payrollSettings.frequency === 'bi-weekly' ? 14 : 7;
            setCurrentPeriodStart(subDays(currentPeriodStart, periodLength));
        }
    };
    const handleNextPeriod = () => {
        if (currentPeriodStart && payrollSettings) {
            const periodLength = payrollSettings.frequency === 'bi-weekly' ? 14 : 7;
            setCurrentPeriodStart(addDays(currentPeriodStart, periodLength));
        }
    };
    const handleCurrentPeriod = () => {
        if (payrollSettings) setCurrentPeriodStart(getCurrentPeriodStart(payrollSettings));
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Top header bar */}
            <div className="bg-white border-b border-slate-200 px-6 py-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-xl font-bold text-slate-800 tracking-tight">Timesheets</h1>
                        {dateRange.start && dateRange.end && (
                            <p className="text-sm text-slate-400 mt-0.5">
                                {format(dateRange.start, 'MMM d')} – {format(dateRange.end, 'MMM d, yyyy')}
                            </p>
                        )}
                    </div>

                    {/* TAB SWITCHER — tsmgr-5a2c8e71: payroll viewers only (Owner/Admin). A Manager has
                        just the Timesheets view, so there is nothing to switch to and no empty tab. */}
                    {canViewPay && (
                        <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
                            <button
                                onClick={() => setActiveTab('timesheets')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'timesheets' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                Timesheets
                            </button>
                            <button
                                onClick={() => setActiveTab('payroll')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'payroll' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <DollarSign size={13} />
                                Payroll Summary
                            </button>
                        </div>
                    )}

                    <div className="flex items-center flex-wrap gap-2">
                        {canApprove && (
                            <select
                                value={selectedEmployeeId}
                                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                                className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            >
                                <option value="all">All Employees</option>
                                {/* 7b5e2d94 — list active users + anyone currently shown (non-active with hours); keep deleted/idle accounts out of the picker */}
                                {employees.filter(emp => emp.id !== currentUserProfile?.id && (isActiveAccount(emp) || timesheetData[emp.id])).map(emp => (
                                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                                ))}
                                {currentUserProfile?.id && (
                                    <option value={currentUserProfile.id}>{currentUserProfile.name} (Me)</option>
                                )}
                            </select>
                        )}

                        {dateRange.start && dateRange.end && (
                            <div className="flex items-center gap-1 border border-slate-200 rounded-lg bg-white p-1">
                                <button onClick={handlePreviousPeriod} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors">
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="text-sm font-medium text-slate-600 px-2 min-w-[130px] text-center">
                                    {format(dateRange.start, 'MMM d')} – {format(dateRange.end, 'MMM d')}
                                </span>
                                <button onClick={handleNextPeriod} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors">
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}

                        <button
                            onClick={handleCurrentPeriod}
                            className="px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                            Current Period
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6 max-w-5xl mx-auto">
                {/* periodtot-3e-strip-9c2a4f10 — the period's hours + approval, ON the Timesheets tab, so a
                    manager sees what they're paying without switching to Payroll Summary. Reads the SAME
                    computePeriodTotals as the Payroll cards (single source — no second truth). Hours only,
                    never gross (that stays the Payroll tab's job). Managers only; only when the period has
                    data. Matches the Payroll "Total Shift Hours"/"Approval Status" cards for the period. */}
                {activeTab !== 'payroll' && canApprove && Object.keys(timesheetData).length > 0 && (() => {
                    const pt = computePeriodTotals(employees, timesheetData, dateRange, orgPayrollDefaults);
                    const gap = pt.totalShiftHours - pt.totalPaidHours;
                    return (
                        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-xs uppercase font-semibold tracking-wide text-slate-400">Period hours</span>
                                <span className="text-lg font-bold text-slate-800 tabular-nums">{pt.totalShiftHours.toFixed(2)}</span>
                                {gap > 0.001 && (
                                    <span className="text-xs font-medium text-amber-600 tabular-nums">· {pt.totalPaidHours.toFixed(2)} paid</span>
                                )}
                            </div>
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-xs uppercase font-semibold tracking-wide text-slate-400">Approved</span>
                                <span className={`text-lg font-bold tabular-nums ${pt.approvedCount === pt.payableCount ? 'text-emerald-600' : 'text-amber-600'}`}>{pt.approvedCount}/{pt.payableCount}</span>
                            </div>
                            {canViewPay && <span className="text-xs text-slate-400">Gross pay is on the Payroll Summary tab.</span>}
                        </div>
                    );
                })()}
                {activeTab === 'payroll' && canViewPay ? ( // tsmgr-5a2c8e71 — belt and braces: no tab button for a Manager either
                    <PayrollSummaryTab
                        timesheetData={timesheetData}
                        employees={employees}
                        dateRange={dateRange}
                        payrollSettings={payrollSettings}
                        currentUserProfile={currentUserProfile}
                        paidRun={paidRun}
                        onMarkAsPaid={handleMarkAsPaid}
                        orgProvince={orgProvince}
                        orgPayrollDefaults={orgPayrollDefaults}
                    />
                ) : isLoading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-slate-200" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-slate-200 rounded w-32" />
                                        <div className="h-3 bg-slate-100 rounded w-20" />
                                    </div>
                                    <div className="flex gap-6">
                                        <div className="h-8 w-12 bg-slate-100 rounded" />
                                        <div className="h-8 w-12 bg-slate-100 rounded" />
                                        <div className="h-8 w-12 bg-slate-100 rounded" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : Object.keys(timesheetData).length > 0 ? (
                    <div className="space-y-3">
                        {Object.entries(timesheetData).map(([userId, data], index) => (
                            <EmployeeCard
                                key={userId}
                                userId={userId}
                                data={data}
                                colorIndex={index}
                                canApprove={canApprove}
                                dateRange={dateRange}
                                onApproveTimesheet={onApproveTimesheet}
                                onJobSelect={onJobSelect}
                                empData={employees.find(e => e.id === userId)}
                                orgPayrollDefaults={orgPayrollDefaults}
                                currentUserProfile={currentUserProfile}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                            <Briefcase size={22} className="text-slate-400" />
                        </div>
                        <p className="text-slate-600 font-medium">No data for this period</p>
                        <p className="text-sm text-slate-400 mt-1">
                            {selectedEmployeeId === 'all'
                                ? 'No shifts or jobs found for the organisation.'
                                : 'No shifts or jobs found for this employee.'}
                        </p>
                        <p className="text-xs text-slate-300 mt-3">
                            If data appears under "All Employees" but not when filtering, a Firestore composite index may be required.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TimesheetsView;